import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Trash2, User, Building2, ArrowUpRight, Check, Download, X, AlertTriangle, MessageCircle, FileSpreadsheet, Info } from "lucide-react";
import { Modal, ModalActions, Field, SelectField, TextArea, MoneyField, MoneyInput, HideDailyToggle } from "../lib/ui";
import { PERM_LABELS } from "./Users";
import { api, money, today, addDays, download, humanDate, normalizePhoneTR, normalizePlateTR, moneyCur, sendProformaWhatsApp, isUnpaidProforma, GOLD_TYPES } from "../lib/api";

// ---------- Simple record modal ----------
export function RecordModal({ onClose, onSaved, initial = {} }) {
  const [form, setForm] = useState({
    title: "", amount: 0, date: today(), category: "Genel",
    type: "income", status: "Bekliyor", note: "", ...initial,
  });
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/records", { ...form, amount: Number(form.amount) });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Kayıt kaydedilemedi");
    }
  };
  return (
    <Modal title="Gelir / gider" onClose={onClose} testid="record-modal-form">
      <form onSubmit={submit}>
        <div className="form-grid two">
          <Field testid="record-title-input" label="İşlem başlığı" value={form.title} required
                 onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn. Ofis kirası" />
          <MoneyField testid="record-amount-input" label="Tutar (₺)" value={form.amount} required
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <SelectField testid="record-type-select" label="Tür" value={form.type}
                       onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="income">Gelir</option>
            <option value="expense">Gider</option>
          </SelectField>
          <SelectField testid="record-status-select" label="Durum" value={form.status}
                       onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option>Bekliyor</option>
            <option>Ödendi</option>
          </SelectField>
          <Field testid="record-date-input" label="Tarih" type="date" value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Field testid="record-category-input" label="Kategori" value={form.category}
                 onChange={(e) => setForm({ ...form, category: e.target.value })} />
        </div>
        <TextArea testid="record-note-input" label="Not" value={form.note} rows="2"
                  onChange={(e) => setForm({ ...form, note: e.target.value })} />
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ---------- Stock modal ----------
export function CategorySelect({ value, onChange, onDeleted, testid = "stock-category-select" }) {
  const [cats, setCats] = useState([]);
  const load = () => api.get("/stock-categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const pick = async (v) => {
    if (v !== "__new__") return onChange(v);
    const name = window.prompt("Yeni kategori adı (örn. Klima, Dizel Isıtıcı, Buzdolabı):", "");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/stock-categories", { name: name.trim() });
      await load();
      onChange(data.name);
    } catch (e) { alert(e.response?.data?.detail || "Kategori eklenemedi"); }
  };
  const removeCat = async () => {
    const c = cats.find((x) => x.name === value);
    if (!c || !window.confirm(`"${c.name}" kategorisi silinsin mi? Bu kategorideki stoklar kategorisiz olur.`)) return;
    try { await api.delete(`/stock-categories/${c.id}`); await load(); onChange(""); onDeleted?.(); }
    catch (e) { alert(e.response?.data?.detail || "Kategori silinemedi"); }
  };
  return (
    <label className="field">
      <span>
        Kategori
        {value && cats.some((x) => x.name === value) && (
          <button type="button" className="text-button cat-del" data-testid={`${testid}-delete`} onClick={removeCat}>Kategoriyi sil</button>
        )}
      </span>
      <select data-testid={testid} value={value || ""} onChange={(e) => pick(e.target.value)}>
        <option value="">— Kategorisiz —</option>
        {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        {value && !cats.some((c) => c.name === value) && <option value={value}>{value}</option>}
        <option value="__new__">+ Yeni kategori ekle</option>
      </select>
    </label>
  );
}

export function StockModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ code: "", name: "", description: "", barcode: "", quantity: 0, unit: "Adet", price: 0, currency: "TRY", kdv: 20, min_level: "", category: "" });
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stocks", {
        ...form,
        quantity: Number(form.quantity),
        price: Number(form.price),
        min_level: form.min_level === "" || form.min_level === null ? null : Number(form.min_level),
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Kaydedilemedi");
    }
  };
  return (
    <Modal title="Stok kartı" onClose={onClose} testid="stock-modal-form">
      <form onSubmit={submit}>
        <div className="form-grid two">
          <Field testid="stock-code-input" label="Stok kodu" value={form.code} required
                 onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="STK-001" />
          <Field testid="stock-name-input" label="Stok adı" value={form.name} required
                 onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ürün adı" />
          <Field testid="stock-barcode-input" label="Barkod" value={form.barcode}
                 onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                 placeholder="Okuyucudan tara veya EAN/UPC gir" />
          <CategorySelect value={form.category} onChange={(v) => setForm({ ...form, category: v })} onDeleted={onSaved} />
          <Field testid="stock-quantity-input" label="Miktar" type="number" step="0.01" value={form.quantity}
                 onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          <Field testid="stock-min-level-input" label="Kritik seviye (boş = genel ayar)" type="number" step="0.01" min="0"
                 value={form.min_level}
                 placeholder="Örn: 5"
                 onChange={(e) => setForm({ ...form, min_level: e.target.value })} />
          <Field testid="stock-unit-input" label="Birim" value={form.unit}
                 onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Adet, kg, m³" />
          <MoneyField testid="stock-price-input"
                 label={`Birim fiyat (${form.currency === "USD" ? "$" : form.currency === "EUR" ? "€" : "₺"})`}
                 value={form.price}
                 onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <SelectField testid="stock-currency-select" label="Para birimi" value={form.currency}
                       onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            <option value="TRY">TRY — Türk Lirası</option>
            <option value="USD">USD — Dolar</option>
            <option value="EUR">EUR — Euro</option>
          </SelectField>
          <SelectField testid="stock-kdv-select" label="KDV oranı" value={form.kdv}
                       onChange={(e) => setForm({ ...form, kdv: Number(e.target.value) })}>
            {KDV_OPTIONS.map((k) => <option key={k} value={k}>%{k}</option>)}
          </SelectField>
        </div>
        <TextArea testid="stock-description-input" label="Açıklama" rows="2" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ---------- Customer modal (also inline) ----------
function InvoiceCustomerIdField({ customer, ruhsat }) {
  const val = customer ? (customer.tax_no ? `VKN ${customer.tax_no}` : customer.tc_no ? `TC ${customer.tc_no}` : "Kayıtlı değil")
    : ruhsat?.owner_id_no ? `Ruhsatta: ${ruhsat.owner_id_no}` : "—";
  return <Field testid="invoice-customer-idno" label="TC / Vergi No" readOnly value={val} />;
}

function RuhsatMatchBanner({ m, onConfirm, onNew }) {
  const src = m.no_vehicle ? "Fişteki" : "Ruhsattaki";
  const srcIn = m.no_vehicle ? "Fişte" : "Ruhsatta";
  if (m.status === "loading") return <div className="proforma-note" data-testid="ruhsat-match-banner">{src} TC / vergi no kontrol ediliyor…</div>;
  const idTxt = m.owner_id_no ? `TC/VKN ${m.owner_id_no}` : (m.no_vehicle ? "TC/VKN girilmemiş" : "TC/VKN okunamadı");
  const box = (tone, children) => (
    <div data-testid="ruhsat-match-banner" data-status={m.status} className="proforma-note"
         style={{ marginBottom: 12, borderColor: tone, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>{children}</div>
  );
  if (m.status === "matched") return box("#059669", <span>
    {m.created ? "Fiş bilgilerine göre yeni cari oluşturuldu: " : m.confirmed ? "Eşleştirildi, eksik bilgiler fişten dolduruldu: " : `${src} ${idTxt} ile kayıtlı cari eşleşti: `}
    <strong>{m.customer.name}</strong></span>);
  if (m.status === "name_only") return box("#d97706", <>
    <span>{(m.customer.tc_no || m.customer.tax_no)
      ? <>{srcIn} TC / vergi no yok; isim kayıtlı cari <strong>{m.customer.name}</strong> ile eşleşiyor. Bu cariyle eşleştirilsin mi?</>
      : <><strong>{m.customer.name}</strong> isimli kayıtlı carinin TC / vergi numarası yok. {srcIn}: {idTxt}. Bu cariyle eşleştirilsin mi?</>}</span>
    <button type="button" className="primary-button" data-testid="ruhsat-match-confirm" onClick={onConfirm}>Eşleştir</button>
    <button type="button" className="secondary-button" data-testid="ruhsat-match-new" onClick={onNew}>Yeni cari oluştur</button>
  </>);
  return box("#d97706", <>
    <span>{src} müşteri (<strong>{m.proposal?.name || "-"}</strong> · {idTxt}) kayıtlarımızda yok. {m.no_vehicle ? "Fiş" : "Ruhsat"} bilgilerine göre yeni cari oluşturulacak.</span>
    <button type="button" className="primary-button" data-testid="ruhsat-match-new" onClick={onNew}>Cari bilgilerini kontrol et ve oluştur</button>
  </>);
}

export function CustomerForm({ form, setForm }) {
  useEffect(() => { if (form.is_cari === undefined) setForm((f) => ({ ...f, is_cari: false })); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className="type-toggle">
        <button type="button" data-testid="customer-type-individual"
                className={form.type === "individual" ? "active" : ""}
                onClick={() => setForm({ ...form, type: "individual" })}>
          <User size={15} /> Şahıs
        </button>
        <button type="button" data-testid="customer-type-corporate"
                className={form.type === "corporate" ? "active" : ""}
                onClick={() => setForm({ ...form, type: "corporate" })}>
          <Building2 size={15} /> Kurumsal
        </button>
      </div>
      <div className="form-grid two">
        <Field testid="customer-name-input" label={form.type === "corporate" ? "Firma ünvanı" : "Ad soyad"} value={form.name} required
               onChange={(e) => setForm({ ...form, name: e.target.value })} />
        {form.type === "individual" ? (
          <Field testid="customer-tc-input" label="TC Kimlik No" value={form.tc_no} maxLength={11}
                 onChange={(e) => setForm({ ...form, tc_no: e.target.value.replace(/\D/g, "") })} />
        ) : (
          <Field testid="customer-tax-input" label="Vergi No" value={form.tax_no}
                 onChange={(e) => setForm({ ...form, tax_no: e.target.value })} />
        )}
        <Field testid="customer-tax-office-input" label="Vergi Dairesi" value={form.tax_office}
               onChange={(e) => setForm({ ...form, tax_office: e.target.value })} />
        <Field testid="customer-phone-input" label="Telefon" value={form.phone}
               hint="Örn: 0505 000 00 00 veya +90 505 000 00 00"
               onChange={(e) => setForm({ ...form, phone: e.target.value })}
               onBlur={(e) => setForm({ ...form, phone: normalizePhoneTR(e.target.value) })} />
        <Field testid="customer-whatsapp-input" label="WhatsApp No" value={form.whatsapp || ""}
               hint="Proforma fişi WhatsApp ile bu numaraya gönderilir"
               onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
               onBlur={(e) => setForm({ ...form, whatsapp: normalizePhoneTR(e.target.value) })} />
        <Field testid="customer-email-input" label="E-posta" type="email" value={form.email}
               onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Field testid="customer-city-input" label="İl" value={form.city}
               onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <Field testid="customer-district-input" label="İlçe" value={form.district}
               onChange={(e) => setForm({ ...form, district: e.target.value })} />
      </div>
      <div className="cari-toggle-row" style={{ marginTop: 10, padding: 12, background: "var(--panel-soft, #f8fafc)", borderRadius: 8, border: "1px solid var(--line)" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          <input type="checkbox" data-testid="customer-is-cari-toggle"
                 checked={form.is_cari !== false}
                 onChange={(e) => setForm({ ...form, is_cari: e.target.checked })} />
          Cari hesap olarak işaretle
          <small style={{ color: "var(--muted)", fontWeight: 400, marginLeft: 8 }}>
            (fatura/ödeme bakiyesi takip edilsin)
          </small>
        </label>
        {form.is_cari !== false && (
          <>
            <div style={{ marginTop: 10 }}>
              <Field testid="customer-payment-term-input"
                     label="Vade süresi (gün)"
                     type="number" min="0" step="1"
                     value={form.payment_term_days ?? 15}
                     onChange={(e) => setForm({ ...form, payment_term_days: Number(e.target.value) || 0 })}
                     hint="Satış faturası düzenlerken vade tarihi bu süreye göre otomatik hesaplanır" />
            </div>
          </>
        )}
      </div>
      <TextArea testid="customer-address-input" label="Adres" rows="2" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} />
    </>
  );
}

export function CustomerModal({ onClose, onSaved, initial, readOnly = false }) {
  const [form, setForm] = useState(() => ({
    type: initial?.type || "individual",
    name: initial?.name || "",
    tc_no: initial?.tc_no || "",
    tax_no: initial?.tax_no || "",
    tax_office: initial?.tax_office || "",
    phone: initial?.phone || "",
    whatsapp: initial?.whatsapp || "",
    email: initial?.email || "",
    address: initial?.address || "",
    city: initial?.city || "",
    district: initial?.district || "",
    note: initial?.note || "",
    is_cari: initial ? initial.is_cari !== false : false,
    payment_term_days: initial?.payment_term_days ?? 15,
  }));
  const [error, setError] = useState("");
  const [similar, setSimilar] = useState(null);
  const isEdit = !!initial?.id;
  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (!isEdit && similar === null) {
        const { data: hits } = await api.post("/customers/similar", { name: form.name, tc_no: form.tc_no, tax_no: form.tax_no }).catch(() => ({ data: [] }));
        if (hits.length) { setSimilar(hits); return; }
      }
      let saved;
      if (isEdit) {
        saved = (await api.put(`/customers/${initial.id}`, form)).data;
      } else {
        saved = (await api.post("/customers", form)).data;
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Cari kaydedilemedi");
    }
  };
  return (
    <Modal title={readOnly ? "Cari bilgileri" : isEdit ? "Cari kart düzenle" : "Cari kart"} size="lg" onClose={onClose} testid="customer-modal-form">
      <form onSubmit={submit}>
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <CustomerForm form={form} setForm={(f) => {
            const next = typeof f === "function" ? f(form) : f;
            if (similar && ["name", "tc_no", "tax_no"].some((k) => next[k] !== form[k])) setSimilar(null);
            setForm(next);
          }} />
        </fieldset>
        {readOnly && <p className="muted" data-testid="customer-readonly-note">Cari bilgilerini düzenleme yetkiniz yok.</p>}
        {similar?.length > 0 && (
          <div className="merge-warn" data-testid="customer-similar-warning">
            <AlertTriangle size={16} />
            <span>Bu cari zaten kayıtlı olabilir: {similar.map((s) => `${s.name} (${s.reason}${s.archived ? ", arşivde" : ""})`).join(" · ")}.
              Yine de yeni kart açmak için tekrar <b>Kaydet</b>'e basın.</span>
          </div>
        )}
        {error && <div className="form-error">{error}</div>}
        {readOnly
          ? <div className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>Kapat</button></div>
          : <ModalActions onCancel={onClose} submitLabel={isEdit ? "Değişiklikleri kaydet" : "Kaydet"} />}
      </form>
    </Modal>
  );
}

export function CustomerInfoButton({ customerId, customers = [], onSaved, testid = "customer-info-btn" }) {
  const [c, setC] = useState(null);
  if (!customerId) return null;
  const u = window.__SP_USER;
  const canEdit = u?.role === "admin" || !!u?.permissions?.edit_customer;
  const open = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await api.get(`/customers/${customerId}/statement`);
      setC(data.customer);
    } catch {
      const local = customers.find((x) => x.id === customerId);
      if (local) setC(local); else alert("Cari bilgileri alınamadı");
    }
  };
  return (
    <>
      <button type="button" className="customer-info-btn" data-testid={testid} title="Cari bilgilerini göster" onClick={open}>
        <Info size={13} /> Bilgiler
      </button>
      {c && createPortal(<CustomerModal initial={c} readOnly={!canEdit} onClose={() => setC(null)} onSaved={(d) => { onSaved?.(d); window.dispatchEvent(new CustomEvent("sp-customer-updated", { detail: d })); }} />, document.body)}
    </>
  );
}

// ---------- Invoice modal ----------
export const KDV_OPTIONS = [0, 1, 8, 10, 18, 20];

export function lineTotal(row) {
  const q = Number(row.quantity || 0);
  const p = Number(row.unit_price || 0);
  const kdv = Number(row.kdv || 0);
  const sub = q * p;
  const total = sub * (1 + kdv / 100);
  return { sub, total, kdvAmount: total - sub };
}

// KDV dahil satır toplamı girilince birim fiyatı (4 hane) geriye hesaplar
export function LineTotalInput({ row, onPatch, testid }) {
  const t = lineTotal(row);
  return (
    <MoneyInput value={Math.round(t.total * 100) / 100} testid={testid} className="line-total-input" title="KDV dahil toplam — yazınca birim fiyat hesaplanır"
                onChange={(e) => {
                  const total = Number(e.target.value || 0);
                  const q = Number(row.quantity || 0) || 1;
                  const unit = total / q / (1 + Number(row.kdv || 0) / 100);
                  onPatch({ unit_price: String(Math.round(unit * 10000) / 10000), ...(Number(row.quantity || 0) ? {} : { quantity: 1 }) });
                }} />
  );
}

export function InvoiceModal({ onClose, onSaved, customers, stocks, accounts = [], services = [], onCustomerAdded, prefill }) {
  const [form, setForm] = useState(() => {
    const base = {
      customer_id: "",
      customer_name: "",
      invoice_no: "",
      invoice_type: "Resmi",
      doc_kind: "fatura",
      date: today(),
      due_date: addDays(today(), 15),
      products: [],
      services: [],
      status: "Ödenecek",
      note: "",
      chassis_no: "",
      plate: "",
      vehicles: [{ plate: "", chassis_no: "" }],
      paid_account_id: "",
      paid_proxy_customer_id: null,
    };
    if (prefill) {
      const match = (customers || []).find((c) => (c.name || "").toLowerCase() === (prefill.customer_name || "").toLowerCase());
      return {
        ...base,
        customer_name: prefill.customer_name || "",
        customer_id: prefill.customer_mismatch ? "" : (match?.id || ""),
        plate: prefill.plate || "",
        chassis_no: prefill.chassis_no || "",
        vehicles: prefill.vehicles?.length
          ? prefill.vehicles.map((v) => ({ plate: normalizePlateTR(v.plate || ""), chassis_no: v.chassis_no || "" }))
          : [{ plate: normalizePlateTR(prefill.plate || ""), chassis_no: prefill.chassis_no || "" }],
        note: prefill.note || "",
        products: (prefill.products || []).map((p) => ({
          stock_id: p.stock_id, source_warehouse: p.source_warehouse || "main", code: p.code || "", name: p.name || "",
          description: p.description || "", quantity: Number(p.quantity) || 1, unit_price: Number(p.unit_price) || 0,
          kdv: Number(p.kdv) || 20, vehicle_idx: p.vehicle_idx ?? 0,
        })),
        services: (prefill.services || []).map((s) => ({
          service_id: s.service_id || null,
          code: s.code || "",
          name: s.name || "",
          description: s.description || "",
          quantity: Number(s.quantity) || 1,
          unit_price: Number(s.unit_price) || 0,
          kdv: Number(s.kdv) || 20,
          vehicle_idx: s.vehicle_idx ?? 0,
        })),
        source_work_order_id: prefill.source_work_order_id || null,
      };
    }
    return base;
  });
  const [error, setError] = useState("");
  const [inlineCustomer, setInlineCustomer] = useState(null);
  const [ruhsatMatch, setRuhsatMatch] = useState(null);
  const woId = prefill?.source_work_order_id;
  useEffect(() => {
    if (!woId) return;
    setRuhsatMatch({ status: "loading" });
    api.post(`/work-orders/${woId}/customer-match`).then(({ data }) => {
      setRuhsatMatch(data);
      if (data.status === "matched") setForm((f) => ({ ...f, customer_id: data.customer.id, customer_name: data.customer.name }));
      else setForm((f) => ({ ...f, customer_id: "", customer_name: "" }));
    }).catch(() => setRuhsatMatch(null));
  }, [woId]);
  const confirmNameMatch = async () => {
    try {
      const { data } = await api.post(`/customers/${ruhsatMatch.customer.id}/fill-missing`, ruhsatMatch.proposal);
      onCustomerAdded(data);
      setForm((f) => ({ ...f, customer_id: data.id, customer_name: data.name }));
      setRuhsatMatch({ ...ruhsatMatch, status: "matched", customer: data, confirmed: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Eşleştirilemedi");
    }
  };
  const openRuhsatNewCustomer = () => setInlineCustomer({
    tax_office: "", phone: "", email: "", city: "", district: "", ...ruhsatMatch.proposal,
  });

  // Proforma: numara otomatik (PRF-YYYYAAGGnnn); Resmi: elle girilir
  useEffect(() => {
    if (form.invoice_type !== "Proforma") return;
    api.get(`/invoices/next-no?invoice_type=Proforma&date=${form.date || today()}`)
      .then((r) => setForm((f) => ({ ...f, invoice_no: r.data.invoice_no })));
    // eslint-disable-next-line
  }, [form.invoice_type, form.date]);
  const paidAccount = accounts.find((a) => a.id === form.paid_account_id);
  const isPosReceipt = form.invoice_type === "Resmi" && form.doc_kind !== "nakit_fis" && form.status === "Ödendi" && paidAccount?.type === "pos";
  const isCashReceipt = form.invoice_type === "Resmi" && form.doc_kind === "nakit_fis";

  const setInvoiceType = (type) => {
    // if switching to Proforma, force all line KDVs to 0
    if (type === "Proforma") {
      setForm((f) => ({
        ...f,
        invoice_type: type,
        doc_kind: "fatura",
        products: f.products.map((p) => ({ ...p, kdv: 0 })),
        services: f.services.map((s) => ({ ...s, kdv: 0 })),
      }));
    } else {
      setForm((f) => ({ ...f, invoice_type: type, invoice_no: f.invoice_type === type ? f.invoice_no : "" }));
    }
  };

  const totals = useMemo(() => {
    let sub = 0, kdvT = 0, total = 0;
    for (const row of [...form.products, ...form.services]) {
      const t = lineTotal(row);
      sub += t.sub;
      kdvT += t.kdvAmount;
      total += t.total;
    }
    return { subtotal: sub, kdv: kdvT, total };
  }, [form.products, form.services]);

  const defaultKdv = form.invoice_type === "Proforma" ? 0 : 20;
  const addProduct = (v = 0) => setForm({ ...form, products: [...form.products, { stock_id: "", source_warehouse: "main", code: "", name: "", description: "", quantity: 1, unit_price: 0, kdv: defaultKdv, vehicle_idx: v }] });
  const addService = (v = 0) => setForm({ ...form, services: [...form.services, { name: "", description: "", quantity: 1, unit_price: 0, kdv: defaultKdv, vehicle_idx: v }] });
  const updateVehicle = (v, patch) => setForm({ ...form, vehicles: form.vehicles.map((x, idx) => (idx === v ? { ...x, ...patch } : x)) });
  const addVehicle = () => setForm({ ...form, vehicles: [...form.vehicles, { plate: "", chassis_no: "" }] });
  const removeVehicle = (v) => {
    const shift = (rows) => rows.filter((r) => (r.vehicle_idx ?? 0) !== v)
      .map((r) => ((r.vehicle_idx ?? 0) > v ? { ...r, vehicle_idx: r.vehicle_idx - 1 } : r));
    setForm({ ...form, vehicles: form.vehicles.filter((_, idx) => idx !== v), products: shift(form.products), services: shift(form.services) });
  };
  const vehicleTotal = (v) => [...form.products, ...form.services]
    .filter((r) => (r.vehicle_idx ?? 0) === v).reduce((sum, r) => sum + lineTotal(r).total, 0);

  const updateProduct = (i, patch) => setForm({ ...form, products: form.products.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const updateService = (i, patch) => setForm({ ...form, services: form.services.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const removeProduct = (i) => setForm({ ...form, products: form.products.filter((_, idx) => idx !== i) });
  const removeService = (i) => setForm({ ...form, services: form.services.filter((_, idx) => idx !== i) });

  const [rates, setRates] = useState(null);
  useEffect(() => {
    api.get("/exchange-rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  // Para birimine göre TRY'ye çevir (fatura kalemleri TRY üzerinden hesaplanır)
  const toTRY = (price, cur) => {
    if (!cur || cur === "TRY") return price;
    if (!rates?.rates) return price;
    const r = rates.rates[cur];
    if (!r) return price;
    return price * (r.forex_selling || r.forex_buying || 1);
  };

  const pickStock = (i, stockId) => {
    const s = stocks.find((x) => x.id === stockId);
    if (!s) return;
    const current = form.products[i] || {};
    const unit = toTRY(Number(s.price || 0), s.currency || "TRY");
    updateProduct(i, {
      stock_id: s.id,
      code: s.code,
      name: s.name,
      unit_price: Number(unit.toFixed(2)),
      kdv: form.invoice_type === "Proforma" ? 0 : (s.kdv ?? 20),
      description: current.description || s.description || "",
      source_currency: s.currency || "TRY",
    });
  };

  const pickCustomer = (id) => {
    if (id === "__new__") {
      setInlineCustomer({
        type: "individual", name: "", tc_no: "", tax_no: "", tax_office: "",
        phone: "", email: "", address: "", city: "", district: "",
      });
      return;
    }
    const c = customers.find((x) => x.id === id);
    const patch = { ...form, customer_id: id, customer_name: c ? c.name : "" };
    // Carinin kendi vade süresi varsa vade tarihini otomatik hesapla
    if (c && c.is_cari !== false && typeof c.payment_term_days === "number") {
      patch.due_date = addDays(form.date || today(), c.payment_term_days);
    }
    setForm(patch);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (ruhsatMatch && ["name_only", "new"].includes(ruhsatMatch.status) && !form.customer_id) {
      return setError(ruhsatMatch.status === "new"
        ? "Bu müşteri kayıtlarda yok. Önce ruhsat bilgilerine göre yeni cariyi oluşturun."
        : "Ruhsattaki müşteri için önce 'Eşleştir' deyin veya yeni cari oluşturun.");
    }
    if (!form.customer_name) return setError("Cari seçin veya yeni cari oluşturun.");
    if (!form.products.length && !form.services.length) return setError("En az bir ürün veya hizmet ekleyin.");
    if (form.status === "Ödendi" && !form.paid_account_id && !form.paid_proxy_customer_id) {
      return setError("Lütfen bir ödeme hesabı veya Cari Hesap seçin.");
    }
    if ([...form.products, ...form.services].some((r) => !String(r.name || "").trim())) {
      return setError("Ürün / hizmet satırlarının adı boş bırakılamaz.");
    }
    if (form.invoice_type === "Resmi" && !isCashReceipt && !String(form.invoice_no || "").trim()) {
      return setError(isPosReceipt ? "POS kasa seçildi: fiş numarasını girin." : "Resmi fatura için fatura numarasını elle girin.");
    }
    try {
      const first = form.vehicles[0] || {};
      const strip = (rows) => rows.map(({ _showNote, ...r }) => r);
      await api.post("/invoices", {
        ...form,
        products: strip(form.products),
        source_work_order_ids: prefill?.source_work_order_ids || [],
        services: strip(form.services),
        plate: first.plate || "",
        chassis_no: first.chassis_no || "",
        paid_proxy_customer_id: form.paid_proxy_customer_id || null,
        subtotal: totals.subtotal,
        kdv_total: totals.kdv,
        total: totals.total,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Fatura kaydedilemedi");
    }
  };

  const submitInlineCustomer = async () => {
    try {
      const { data } = await api.post("/customers", inlineCustomer);
      onCustomerAdded(data);
      setForm({ ...form, customer_id: data.id, customer_name: data.name });
      setInlineCustomer(null);
      if (ruhsatMatch && ruhsatMatch.status !== "matched") setRuhsatMatch({ ...ruhsatMatch, status: "matched", customer: data, created: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Cari eklenemedi");
    }
  };

  const selectedCustomer = customers.find((c) => c.id === form.customer_id);
  const custBalance = selectedCustomer ? Number(selectedCustomer.balance || 0) : 0;
  const custResmi = selectedCustomer ? Number(selectedCustomer.resmi_balance || 0) : 0;
  const custProforma = selectedCustomer ? Number(selectedCustomer.proforma_balance || 0) : 0;

  return (
    <Modal title="Yeni fatura" size="xl" onClose={onClose} testid="invoice-modal-form">
      <form onSubmit={submit}>
        <div className="type-toggle">
          <button data-testid="invoice-type-resmi" type="button"
                  className={form.invoice_type === "Resmi" ? "active" : ""}
                  onClick={() => setInvoiceType("Resmi")}>Resmi Fatura</button>
          <button data-testid="invoice-type-proforma" type="button"
                  className={form.invoice_type === "Proforma" ? "active positive" : ""}
                  onClick={() => setInvoiceType("Proforma")}>Proforma Fatura</button>
        </div>
        {form.invoice_type === "Proforma" && (
          <div className="proforma-note" data-testid="proforma-note">
            Proforma faturada tüm kalemlerin KDV oranı otomatik olarak <strong>%0</strong> olur ve cari ekstrede <strong>Proforma bakiyesi</strong> altında görünür.
          </div>
        )}
        {form.invoice_type === "Resmi" && (
          <label className="cash-receipt-toggle" data-testid="cash-receipt-toggle-label">
            <input type="checkbox" data-testid="cash-receipt-toggle" checked={isCashReceipt}
                   onChange={(e) => setForm({ ...form, doc_kind: e.target.checked ? "nakit_fis" : "fatura", invoice_no: "",
                                              status: e.target.checked ? "Ödendi" : form.status })} />
            <span><strong>Nakit fiş</strong> — ödeme nakit alındı ve nakit fiş verildi (fatura / fiş no girilmez)</span>
          </label>
        )}
        {isPosReceipt && (
          <div className="proforma-note" data-testid="pos-receipt-note">POS kasa seçildi — fatura numarası yerine <strong>fiş numarası</strong> girin.</div>
        )}
        {selectedCustomer && Math.abs(custBalance) > 0.01 && (
          <div className={custBalance > 0 ? "customer-balance-alert debtor" : "customer-balance-alert creditor"}
               data-testid="customer-balance-alert">
            <AlertTriangle size={16} />
            <div>
              <strong>
                {custBalance > 0
                  ? `Bu carinin ${money(custBalance)} borcu bulunuyor!`
                  : `Bu carinin ${money(Math.abs(custBalance))} alacağı bulunuyor.`}
              </strong>
              <small>
                Resmi: {money(custResmi)} · Proforma: {money(custProforma)} — Faturayı kesmeden önce bakiyeyi göz önünde bulundurun.
              </small>
            </div>
          </div>
        )}
        {prefill?.customer_mismatch && !form.customer_id && (
          <div data-testid="wo-customer-mismatch" style={{ border: "1px solid #fcd34d", background: "#fffbeb", color: "#92400e", borderRadius: 10, padding: "10px 14px", marginBottom: 12 }}>
            Seçilen iş fişleri farklı müşterilere ait: <strong>{prefill.customer_mismatch.join(", ")}</strong>. Faturanın kesileceği cariyi aşağıdan elle seçin.
          </div>
        )}
        {ruhsatMatch && <RuhsatMatchBanner m={ruhsatMatch} onConfirm={confirmNameMatch} onNew={openRuhsatNewCustomer} />}
        {ruhsatMatch && error && <div className="form-error" data-testid="ruhsat-match-error" style={{ marginBottom: 12 }}>{error}</div>}
        <div className="form-grid three">
          <CustomerCombobox
            customers={customers}
            selectedId={form.customer_id}
            selectedName={form.customer_name}
            onSelect={(c) => {
              const patch = { ...form, customer_id: c.id, customer_name: c.name };
              const full = customers.find((x) => x.id === c.id);
              if (full && full.is_cari !== false && typeof full.payment_term_days === "number") {
                patch.due_date = addDays(form.date || today(), full.payment_term_days);
              }
              setForm(patch);
            }}
            onCreateNew={() => setInlineCustomer({
              type: "individual", name: "", tc_no: "", tax_no: "", tax_office: "",
              phone: "", email: "", address: "", city: "", district: "",
            })}
          />
          <InvoiceCustomerIdField customer={customers.find((x) => x.id === form.customer_id)} ruhsat={ruhsatMatch} />
          {form.invoice_type === "Proforma" ? (
            <Field testid="invoice-no-input" label="Proforma no (otomatik)" value={form.invoice_no} readOnly
                   hint="Yıl-ay-gün + günlük sıra no" />
          ) : isCashReceipt ? (
            <div className="field cash-receipt-info" data-testid="cash-receipt-info">
              <span className="field-label">Belge</span>
              <strong>Nakit fiş — numara girilmez</strong>
            </div>
          ) : (
            <Field testid="invoice-no-input" label={isPosReceipt ? "Fiş no *" : "Fatura no *"} required value={form.invoice_no}
                   placeholder={isPosReceipt ? "POS fiş numarası" : "Fatura numarasını girin"}
                   onChange={(e) => setForm({ ...form, invoice_no: e.target.value.toUpperCase() })} />
          )}
          <SelectField testid="invoice-status-select" label="Durum" value={form.status}
                       onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option>Ödenecek</option>
            <option>Ödendi</option>
          </SelectField>
          <Field testid="invoice-date-input" label="Fatura tarihi" type="date" value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
          {form.status !== "Ödendi" && (
            <Field testid="invoice-due-input" label="Vade tarihi" type="date" value={form.due_date}
                   onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          )}
          {form.status === "Ödendi" && (
            <SelectField testid="invoice-paid-account-select" label="Ödeme hesabı"
                         value={form.paid_account_id || (form.paid_proxy_customer_id !== null ? "__proxy__" : "")}
                         onChange={(e) => {
                           const v = e.target.value;
                           if (v === "__proxy__") {
                             setForm({ ...form, paid_account_id: "", paid_proxy_customer_id: form.paid_proxy_customer_id || "" });
                           } else {
                             setForm({ ...form, paid_account_id: v, paid_proxy_customer_id: null });
                           }
                         }}>
              <option value="">— Hesap seçin —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · {a.type === "cash" ? "Kasa" : a.type === "pos" ? "POS" : a.bank_name || "Banka"}
                </option>
              ))}
              <option value="__proxy__" data-testid="paid-account-option-proxy">Cari Hesap (başka bir carinin kartı/hesabı)</option>
            </SelectField>
          )}
        </div>

        {form.status === "Ödendi" && form.paid_proxy_customer_id !== null && (
          <div className="proxy-customer-section" data-testid="proxy-customer-section" style={{ marginTop: "0.75rem" }}>
            <SelectField testid="invoice-proxy-customer-select"
                         label="Ödemenin düşüleceği cari (borçlu olduğunuz firma)"
                         value={form.paid_proxy_customer_id}
                         onChange={(e) => setForm({ ...form, paid_proxy_customer_id: e.target.value })}>
              <option value="">— Cari seçin —</option>
              {customers
                .filter((c) => c.id !== form.customer_id)
                .map((c) => {
                  const bal = Number(c.balance || 0);
                  const label = bal < 0
                    ? `${c.name} · Alacağı: ${Math.abs(bal).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`
                    : `${c.name} · Bakiye: ${bal.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`;
                  return <option key={c.id} value={c.id}>{label}</option>;
                })}
            </SelectField>
            <div className="proforma-note" style={{ marginTop: "0.5rem" }}>
              Seçilen carinin bakiyesinden fatura tutarı kadar düşülecek (ör. kart A müşterisinin kartı ise bile POS'u B firmasına ait olduğundan B'ye olan borcunuz azalacak).
            </div>
          </div>
        )}

        {inlineCustomer && (
          <div className="inline-customer">
            <div className="inline-head">
              <strong>Yeni cari</strong>
              <button type="button" className="text-button" onClick={() => setInlineCustomer(null)}>Vazgeç</button>
            </div>
            <CustomerForm form={inlineCustomer} setForm={setInlineCustomer} />
            <div className="inline-actions">
              <button data-testid="inline-customer-save" type="button" className="primary-button" onClick={submitInlineCustomer}>
                <Check size={15} /> Cariyi kaydet
              </button>
            </div>
          </div>
        )}

        {[...form.vehicles.map((veh, v) => ({ veh, v })), { veh: null, v: -1 }].map(({ veh, v }) => (
          <div key={v} className={v === -1 ? "vehicle-block free-block" : "vehicle-block"} data-testid={v === -1 ? "vehicle-block-free" : `vehicle-block-${v}`}>
            {v === -1 ? (
              <div className="vehicle-head">
                <div className="vehicle-title">
                  <span className="vehicle-badge free">Araçsız kalemler</span>
                  <small>Elden verilen / araca bağlı olmayan ürün ve hizmetler · {money(vehicleTotal(-1))}</small>
                </div>
              </div>
            ) : (
            <div className="vehicle-head">
              <div className="vehicle-title">
                <span className="vehicle-badge">Araç {v + 1}</span>
                {<small data-testid={`vehicle-total-${v}`}>Araç toplamı: {money(vehicleTotal(v))}</small>}
              </div>
              <div className="vehicle-fields">
                <Field testid={v === 0 ? "invoice-plate-input" : `invoice-plate-input-${v}`} label="Plaka" value={veh.plate}
                       onChange={(e) => updateVehicle(v, { plate: e.target.value.toUpperCase() })}
                       onBlur={(e) => updateVehicle(v, { plate: normalizePlateTR(e.target.value) })}
                       placeholder="35 SS 3535" />
                <Field testid={v === 0 ? "invoice-chassis-input" : `invoice-chassis-input-${v}`} label="Şase numarası" value={veh.chassis_no}
                       onChange={(e) => updateVehicle(v, { chassis_no: e.target.value.toUpperCase() })} placeholder="VF7..." />
              </div>
              <button type="button" className="row-icon tone-danger" data-testid={`remove-vehicle-${v}`}
                      title="Aracı ve kalemlerini kaldır" onClick={() => removeVehicle(v)}><Trash2 size={14} /></button>
            </div>
            )}
            <div className="line-section">
              <div className="line-head">
                <div>
                  <strong>Satılan ürünler</strong>
                  <p>Stok kartlarından seçim yapın</p>
                </div>
                <button data-testid={v === 0 ? "add-product-line" : `add-product-line-${v === -1 ? "free" : v}`} type="button" className="secondary-button" onClick={() => addProduct(v)}>
                  <Plus size={14} /> Ürün satırı
                </button>
              </div>
              {form.products.some((r) => (r.vehicle_idx ?? 0) === v) ? (
                <div className="line-table">
                  <div className="line-row product line-header">
                    <span>Depo</span><span>Stok (kod/ad/açıklama ara)</span><span>Açıklama / Seri No</span><span>Miktar</span><span>Birim ₺</span><span>KDV %</span><span>Toplam</span><span></span>
                  </div>
                  {form.products.map((row, i) => {
                    if ((row.vehicle_idx ?? 0) !== v) return null;
                    const t = lineTotal(row);
                    const selectedLabel = row.stock_id ? `${row.code || ""} · ${row.name || ""}` : "";
                    const sw = row.source_warehouse || "main";
                    const filteredStocks = sw === "warranty"
                      ? stocks.filter((s) => (s.warranty_quantity || 0) > 0)
                      : stocks;
                    return (
                      <div key={i} className={`line-row product ${sw === "warranty" ? "wh-warranty" : ""}`} data-testid={`product-line-${i}`}>
                        <select value={sw}
                                data-testid={`product-warehouse-${i}`}
                                onChange={(e) => updateProduct(i, { source_warehouse: e.target.value, stock_id: "", code: "", name: "", unit_price: 0 })}>
                          <option value="main">Ana</option>
                          <option value="warranty">Garanti</option>
                        </select>
                        <StockCombobox
                          stocks={filteredStocks}
                          selectedId={row.stock_id}
                          selectedLabel={selectedLabel}
                          onSelect={(s) => pickStock(i, s.id)}
                          index={i}
                        />
                        <input placeholder="Seri no, açıklama" data-testid={`product-description-${i}`}
                               value={row.description || ""} onChange={(e) => updateProduct(i, { description: e.target.value })} />
                        <input type="number" step="0.01" value={row.quantity} data-testid={`product-qty-${i}`} onChange={(e) => updateProduct(i, { quantity: e.target.value })} />
                        <MoneyInput decimals={4} value={row.unit_price} onChange={(e) => updateProduct(i, { unit_price: e.target.value })} data-testid={`product-price-${i}`} />
                        <select value={row.kdv} onChange={(e) => updateProduct(i, { kdv: e.target.value })}
                                disabled={form.invoice_type === "Proforma"}
                                title={form.invoice_type === "Proforma" ? "Proforma fatura için KDV %0" : ""}>
                          {KDV_OPTIONS.map((k) => <option key={k} value={k}>%{k}</option>)}
                        </select>
                        <LineTotalInput row={row} testid={`product-total-${i}`} onPatch={(p) => updateProduct(i, p)} />
                        <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                          <button type="button"
                                  data-testid={`product-note-toggle-${i}`}
                                  className={`line-item-desc-toggle ${(row._showNote ?? !!row.extra_note) ? "active" : ""}`}
                                  title="Satıra ek açıklama ekle / kaldır"
                                  onClick={() => updateProduct(i, { _showNote: !(row._showNote ?? !!row.extra_note) })}>
                            <Plus size={13} />
                          </button>
                          <button type="button" className="row-icon tone-danger" onClick={() => removeProduct(i)}><Trash2 size={13} /></button>
                        </span>
                        {(row._showNote ?? !!row.extra_note) && (
                          <div className="line-item-desc-row">
                            <textarea data-testid={`product-note-${i}`}
                                      placeholder="Bu ürün kalemi için ek açıklama (faturada görünür, aramada bulunabilir)"
                                      value={row.extra_note || ""}
                                      onChange={(e) => updateProduct(i, { extra_note: e.target.value })} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : <div className="line-empty">Henüz ürün eklenmedi.</div>}
            </div>

            <div className="line-section">
              <div className="line-head">
                <div>
                  <strong>Verilen hizmetler</strong>
                  <p>Hizmet kalemleri ve açıklamaları</p>
                </div>
                <button data-testid={v === 0 ? "add-service-line" : `add-service-line-${v === -1 ? "free" : v}`} type="button" className="secondary-button" onClick={() => addService(v)}>
                  <Plus size={14} /> Hizmet satırı
                </button>
              </div>
              {form.services.some((r) => (r.vehicle_idx ?? 0) === v) ? (
                <div className="line-table">
                  <div className="line-row service line-header">
                    <span>Hizmet (kod/ad/açıklama ara)</span><span>Miktar</span><span>Birim ₺</span><span>KDV %</span><span>Toplam</span><span></span>
                  </div>
                  {form.services.map((row, i) => {
                    if ((row.vehicle_idx ?? 0) !== v) return null;
                    const t = lineTotal(row);
                    const selectedLabel = row.name ? (row.service_id ? `${row.code || ""} · ${row.name}` : row.name) : "";
                    const pickService = (s) => updateService(i, {
                      service_id: s.id, code: s.code, name: s.name,
                      unit_price: s.price,
                      kdv: form.invoice_type === "Proforma" ? 0 : (s.kdv ?? 20),
                      description: row.description || s.description || "",
                    });
                    return (
                      <div key={i} className="line-row service" data-testid={`service-line-${i}`}>
                        <ServiceCombobox
                          services={services}
                          selectedLabel={selectedLabel}
                          onSelect={pickService}
                          index={i}
                        />
                        <input type="number" step="0.01" value={row.quantity} onChange={(e) => updateService(i, { quantity: e.target.value })} />
                        <MoneyInput decimals={4} value={row.unit_price} onChange={(e) => updateService(i, { unit_price: e.target.value })} data-testid={`service-price-line-${i}`} />
                        <select value={row.kdv} onChange={(e) => updateService(i, { kdv: e.target.value })}
                                disabled={form.invoice_type === "Proforma"}>
                          {KDV_OPTIONS.map((k) => <option key={k} value={k}>%{k}</option>)}
                        </select>
                        <LineTotalInput row={row} testid={`service-total-${i}`} onPatch={(p) => updateService(i, p)} />
                        <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                          <button type="button"
                                  data-testid={`service-note-toggle-${i}`}
                                  className={`line-item-desc-toggle ${(row._showNote ?? !!row.extra_note) ? "active" : ""}`}
                                  title="Satıra ek açıklama ekle / kaldır"
                                  onClick={() => updateService(i, { _showNote: !(row._showNote ?? !!row.extra_note) })}>
                            <Plus size={13} />
                          </button>
                          <button type="button" className="row-icon tone-danger" onClick={() => removeService(i)}><Trash2 size={13} /></button>
                        </span>
                        {(row._showNote ?? !!row.extra_note) && (
                          <div className="line-item-desc-row">
                            <textarea data-testid={`service-note-${i}`}
                                      placeholder="Bu hizmet kalemi için ek açıklama (faturada görünür)"
                                      value={row.extra_note || ""}
                                      onChange={(e) => updateService(i, { extra_note: e.target.value })} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : <div className="line-empty">Henüz hizmet eklenmedi.</div>}
            </div>

          </div>
        ))}
        <button type="button" className="secondary-button add-vehicle-btn" data-testid="add-vehicle-button" onClick={addVehicle}>
          <Plus size={14} /> Araç ekle (yeni sayfa)
        </button>

        <div className="invoice-totals">
          <div><span>Ara toplam</span><strong>{money(totals.subtotal)}</strong></div>
          <div><span>KDV</span><strong>{money(totals.kdv)}</strong></div>
          <div className="grand"><span>Genel toplam</span><strong>{money(totals.total)}</strong></div>
        </div>

        <TextArea testid="invoice-note-input" label="Fatura notu" rows="2" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })} />
        <HideDailyToggle form={form} setForm={setForm} testid="invoice-hide-daily" />

        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel="Faturayı kaydet" />
      </form>
    </Modal>
  );
}

// ---------- Invoice view ----------
export function invoiceGroups(inv) {
  const vs = inv.vehicles?.length ? inv.vehicles : [{ plate: inv.plate, chassis_no: inv.chassis_no }];
  const idx = (r) => r.vehicle_idx ?? 0;
  const ps = inv.products || [], ss = inv.services || [];
  const hasFree = [...ps, ...ss].some((r) => idx(r) === -1);
  if (vs.length <= 1 && !hasFree) return [{ title: null, products: ps, services: ss }];
  const groups = vs.map((v, i) => ({
    title: `Araç ${i + 1}: ${v.plate || "—"} · Şase: ${v.chassis_no || "—"}`,
    products: ps.filter((r) => idx(r) === i),
    services: ss.filter((r) => idx(r) === i),
  })).filter((g) => g.products.length || g.services.length || vs.length > 1);
  if (hasFree) groups.push({ title: "Araçsız kalemler (elden satış)", products: ps.filter((r) => idx(r) === -1), services: ss.filter((r) => idx(r) === -1) });
  return groups;
}

function InvoiceLinesView({ products, services }) {
  return (
    <>
    {products?.length > 0 && (
      <>
        <h4>Ürünler</h4>
        <table className="view-table">
          <thead><tr><th>Ürün</th><th>Açıklama</th><th>Miktar</th><th>Birim</th><th>KDV</th><th className="amount">Toplam</th></tr></thead>
          <tbody>
            {products.map((p, i) => (
              <tr key={i}><td>{p.code} · {p.name}</td><td>{p.description || "—"}</td><td>{p.quantity}</td><td>{money(p.unit_price)}</td><td>%{p.kdv}</td>
                <td className="amount">{money(lineTotal(p).total)}</td></tr>
            ))}
          </tbody>
        </table>
      </>
    )}
    {services?.length > 0 && (
      <>
        <h4>Hizmetler</h4>
        <table className="view-table">
          <thead><tr><th>Hizmet</th><th>Miktar</th><th>Birim</th><th>KDV</th><th className="amount">Toplam</th></tr></thead>
          <tbody>
            {services.map((s, i) => (
              <tr key={i}><td>{s.name}</td><td>{s.quantity}</td><td>{money(s.unit_price)}</td><td>%{s.kdv}</td>
                <td className="amount">{money(lineTotal(s).total)}</td></tr>
            ))}
          </tbody>
        </table>
      </>
    )}
    </>
  );
}

export function InvoiceViewModal({ invoice, onClose, onConverted }) {
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState("");
  const doConvert = async () => {
    const no = window.prompt("Resmi fatura numarasını girin (zorunlu). Tüm KDV oranları %20 olarak yeniden hesaplanacak:", "");
    if (no === null) return;
    if (!no.trim()) return setConvertError("Resmi fatura için fatura numarası zorunludur");
    setConverting(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/convert-to-resmi`, { invoice_no: no.trim().toUpperCase() });
      onConverted?.(data);
      onClose();
    } catch (err) {
      setConvertError(err.response?.data?.detail || "Dönüştürme başarısız");
      setConverting(false);
    }
  };
  return (
    <Modal title={`${invoice.invoice_type || "Resmi"} fatura · ${invoice.invoice_no}`} size="lg" onClose={onClose} testid="invoice-view">
      <div className="invoice-view">
        <div className="view-grid">
          <div><span>Cari</span><strong>{invoice.customer_name}</strong></div>
          <div><span>Fatura türü</span><strong data-testid="view-invoice-type">{invoice.invoice_type || "Resmi"}</strong></div>
          {invoice.doc_kind && invoice.doc_kind !== "fatura" && (
            <div><span>Belge</span><strong data-testid="view-doc-kind">{invoice.doc_kind === "pos_fis" ? "POS fişi" : "Nakit fiş"}</strong></div>
          )}
          <div><span>Durum</span><strong>{invoice.status}</strong></div>
          <div><span>Tarih</span><strong>{invoice.date}</strong></div>
          <div><span>Vade</span><strong>{invoice.due_date}</strong></div>
          {(invoice.vehicles || []).length <= 1 && (invoice.chassis_no || invoice.plate) && (<>
            <div><span>Şase No</span><strong>{invoice.chassis_no || "—"}</strong></div>
            <div><span>Plaka</span><strong>{invoice.plate || "—"}</strong></div>
          </>)}
        </div>
        {invoiceGroups(invoice).map((g, gi) => (
          <div key={gi} className={g.title ? "vehicle-view-block" : ""} data-testid={g.title ? `view-vehicle-${gi}` : undefined}>
            {g.title && <h4 className="vehicle-view-title">{g.title}</h4>}
            <InvoiceLinesView products={g.products} services={g.services} />
          </div>
        ))}
        <div className="invoice-totals">
          <div><span>Ara toplam</span><strong>{money(invoice.subtotal)}</strong></div>
          <div><span>KDV</span><strong>{money(invoice.kdv_total)}</strong></div>
          <div className="grand"><span>Toplam</span><strong>{money(invoice.total)}</strong></div>
        </div>
        {invoice.note && <p className="view-note">Not: {invoice.note}</p>}
        {convertError && <div className="form-error">{convertError}</div>}
        <div className="modal-actions">
          {invoice.invoice_type === "Proforma" && !invoice.converted_to && (
            <button data-testid="convert-to-resmi-button" type="button" className="secondary-button"
                    disabled={converting} onClick={doConvert}>
              <ArrowUpRight size={15} /> {converting ? "Dönüştürülüyor..." : "Resmi Faturaya Dönüştür"}
            </button>
          )}
          {isUnpaidProforma(invoice) && (
            <button data-testid="invoice-whatsapp-button" type="button" className="secondary-button whatsapp-btn"
                    onClick={() => sendProformaWhatsApp(invoice).catch((e) => setConvertError(e.response?.data?.detail || "WhatsApp linki oluşturulamadı"))}>
              <MessageCircle size={15} /> WhatsApp'tan Gönder
            </button>
          )}
          <button data-testid="invoice-pdf-download" type="button" className="secondary-button"
                  onClick={() => download(`/invoices/${invoice.id}/pdf`, `fatura-${invoice.invoice_no}.pdf`)}>
            <Download size={15} /> PDF indir
          </button>
          <button type="button" className="primary-button" onClick={() => window.print()}>
            Yazdır <ArrowUpRight size={15} />
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Account modal ----------
export function AccountModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", type: "bank", bank_name: "", iban: "", currency: "TRY", gold_unit: "gr", opening_balance: 0, off_balance: false, note: "" });
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounts", { ...form, opening_balance: Number(form.opening_balance) });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Hesap kaydedilemedi");
    }
  };
  return (
    <Modal title="Banka & Kasa hesabı" onClose={onClose} testid="account-modal-form">
      <form onSubmit={submit}>
        <div className="type-toggle">
          <button data-testid="account-type-bank" type="button" className={form.type === "bank" ? "active" : ""} onClick={() => setForm({ ...form, type: "bank" })}>Banka</button>
          <button data-testid="account-type-cash" type="button" className={form.type === "cash" ? "active" : ""} onClick={() => setForm({ ...form, type: "cash" })}>Kasa</button>
          <button data-testid="account-type-pos" type="button" className={form.type === "pos" ? "active" : ""} onClick={() => setForm({ ...form, type: "pos" })}>POS</button>
        </div>
        <div className="form-grid two">
          <Field testid="account-name-input" label="Hesap adı" value={form.name} required
                 onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ana hesap" />
          {form.type === "bank" && (
            <Field testid="account-bank-input" label="Banka" value={form.bank_name}
                   onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="Ziraat, İş, Garanti..." />
          )}
          {form.type === "bank" && (
            <Field testid="account-iban-input" label="IBAN" value={form.iban}
                   onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="TR..." />
          )}
          <SelectField testid="account-currency-select" label="Para birimi" value={form.currency}
                       onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            <option value="TRY">TRY — Türk Lirası</option>
            <option value="USD">USD — Dolar</option>
            <option value="EUR">EUR — Euro</option>
            <option value="XAU">Altın</option>
          </SelectField>
          {form.currency === "XAU" && (
            <SelectField testid="account-gold-unit-select" label="Altın birimi" value={form.gold_unit}
                         onChange={(e) => setForm({ ...form, gold_unit: e.target.value })}>
              <option value="gr">Gram (gr)</option>
              <option value="adet">Adet (çeyrek, tam, kulplu vb.)</option>
            </SelectField>
          )}
          {form.currency === "XAU" && (
            <SelectField testid="account-gold-type-select" label="Altın cinsi (TL karşılığı için)" value={form.gold_type || "GRA"}
                         onChange={(e) => setForm({ ...form, gold_type: e.target.value })}>
              {GOLD_TYPES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </SelectField>
          )}
          <MoneyField testid="account-opening-input" label={`Açılış bakiyesi (${form.currency === "XAU" ? (form.gold_unit === "adet" ? "adet" : "gr") : form.currency})`} value={form.opening_balance}
                 onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
        </div>
        <div style={{ marginTop: 10, padding: 10, background: "var(--panel-soft, #f8fafc)", borderRadius: 7, border: "1px solid var(--line)" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
            <input type="checkbox" data-testid="account-off-balance-toggle"
                   checked={!!form.off_balance}
                   onChange={(e) => setForm({ ...form, off_balance: e.target.checked })} />
            Bakiye tutmayan özel kasa (cepten harcama)
            <small style={{ color: "var(--muted)", fontWeight: 400, marginLeft: 8 }}>
              (Toplam bakiyeye dahil edilmez, sadece dönem harcama raporu için)
            </small>
          </label>
        </div>
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ---------- Transaction modal ----------
export function TransactionModal({ onClose, onSaved, accounts, customers, defaultAccountId }) {
  const [form, setForm] = useState({
    account_id: defaultAccountId || accounts[0]?.id || "",
    direction: "in", amount: 0, date: today(),
    category: "Tahsilat", description: "", customer_id: "",
    commission: 0,
  });
  const [error, setError] = useState("");
  const selectedAccount = accounts.find((a) => a.id === form.account_id);
  const isBank = selectedAccount?.type === "bank";
  const custName = customers.find((c) => c.id === form.customer_id)?.name;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.account_id) return setError("Önce bir hesap oluşturun.");
    if (Number(form.commission) < 0) return setError("Komisyon negatif olamaz.");
    try {
      await api.post("/transactions", {
        account_id: form.account_id, direction: form.direction,
        amount: Number(form.amount), date: form.date,
        category: form.category, description: form.description,
        customer_id: form.customer_id || null, hide_daily: !!form.hide_daily,
      });
      // Komisyon: ayrı gider — cari bakiyesine dokunmaz, sadece bankadan düşer
      const commission = Number(form.commission) || 0;
      if (commission > 0 && isBank) {
        try {
          await api.post("/transactions", {
            account_id: form.account_id, direction: "out",
            amount: commission, date: form.date,
            category: "Komisyon",
            description: `Banka komisyonu${custName ? " · " + custName : ""}${form.description ? " · " + form.description : ""}`,
            customer_id: null,
          });
        } catch (ex2) {
          onSaved();
          setError("Hareket kaydedildi ancak banka komisyonu kaydedilemedi: " +
                   (ex2.response?.data?.detail || ex2.message || "bilinmeyen hata"));
          return;
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Kaydedilemedi");
    }
  };
  return (
    <Modal title="Yeni hareket" onClose={onClose} testid="transaction-modal-form">
      <form onSubmit={submit}>
        <div className="type-toggle">
          <button data-testid="tx-in" type="button" className={form.direction === "in" ? "active positive" : ""} onClick={() => setForm({ ...form, direction: "in", category: "Tahsilat" })}>Tahsilat / Giriş</button>
          <button data-testid="tx-out" type="button" className={form.direction === "out" ? "active negative" : ""} onClick={() => setForm({ ...form, direction: "out", category: "Ödeme" })}>Ödeme / Çıkış</button>
        </div>
        <div className="form-grid two">
          <SelectField testid="tx-account-select" label="Hesap" value={form.account_id}
                       onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </SelectField>
          <SelectField testid="tx-customer-select" label={<>Cari <CustomerInfoButton customerId={form.customer_id} customers={customers} testid="tx-customer-info" /></>} value={form.customer_id}
                       onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
            <option value="">— Seçilmedi —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SelectField>
          <MoneyField testid="tx-amount-input" label="Tutar (₺)" value={form.amount} required
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <Field testid="tx-date-input" label="Tarih" type="date" value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Field testid="tx-category-input" label="Kategori" value={form.category}
                 onChange={(e) => setForm({ ...form, category: e.target.value })} />
        </div>
        {isBank && (
          <div className="form-grid two" style={{ marginTop: 6 }}>
            <MoneyField testid="tx-commission-input" label="Banka komisyonu (₺) — opsiyonel"
                   value={form.commission}
                   onChange={(e) => setForm({ ...form, commission: e.target.value })}
                   placeholder="0,00" />
            <div className="muted" style={{ fontSize: 11.5, alignSelf: "flex-end", paddingBottom: 10 }} data-testid="tx-commission-hint">
              Bu tutar hareketle ilişkilendirilmez; yalnızca bu banka hesabından "Komisyon" gideri olarak düşer.
            </div>
          </div>
        )}
        <TextArea testid="tx-description-input" label="Açıklama" rows="2" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <HideDailyToggle form={form} setForm={setForm} testid="tx-hide-daily" />
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ---------- Cheque modal ----------
export function ChequeModal({ onClose, onSaved, customers, cheques = [] }) {
  const [mode, setMode] = useState("new"); // new | endorse
  const [sourceChequeId, setSourceChequeId] = useState("");
  const [form, setForm] = useState({
    direction: "received", check_no: "", bank_name: "", branch: "",
    customer_id: "", customer_name: "", amount: 0,
    issue_date: today(), maturity_date: addDays(today(), 30),
    status: "Portföyde", note: "",
  });
  const [error, setError] = useState("");

  // available received cheques in portfolio (for endorse)
  const portfolioCheques = useMemo(
    () => cheques.filter((c) => c.direction === "received" && c.status === "Portföyde"),
    [cheques]
  );

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm({ ...form, customer_id: id, customer_name: c ? c.name : "" });
  };

  const pickSourceCheque = (id) => {
    setSourceChequeId(id);
    const src = portfolioCheques.find((c) => c.id === id);
    if (src) {
      setForm({
        ...form,
        direction: "given",
        check_no: src.check_no || "",
        bank_name: src.bank_name || "",
        branch: src.branch || "",
        amount: src.amount || 0,
        issue_date: src.issue_date || today(),
        maturity_date: src.maturity_date || addDays(today(), 30),
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === "endorse") {
        if (!sourceChequeId) return setError("Ciro edilecek çeki seçin");
        if (!form.customer_id) return setError("Ciro edilecek cariyi seçin");
        await api.post(`/cheques/${sourceChequeId}/endorse`, {
          customer_id: form.customer_id,
          customer_name: form.customer_name,
          date: today(),
          note: form.note,
        });
      } else {
        await api.post("/cheques", { ...form, amount: Number(form.amount) });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Kaydedilemedi");
    }
  };
  return (
    <Modal title={mode === "endorse" ? "Çek ciro et" : "Çek kartı"} size="lg" onClose={onClose} testid="cheque-modal-form">
      <form onSubmit={submit}>
        <div className="type-toggle">
          <button data-testid="cheque-mode-new" type="button"
                  className={mode === "new" ? "active" : ""}
                  onClick={() => { setMode("new"); setSourceChequeId(""); }}>
            Yeni Çek
          </button>
          <button data-testid="cheque-mode-endorse" type="button"
                  className={mode === "endorse" ? "active positive" : ""}
                  onClick={() => { setMode("endorse"); setForm({ ...form, direction: "given" }); }}>
            Portföydeki Çeki Ciro Et
          </button>
        </div>

        {mode === "endorse" && (
          <>
            <div className="form-grid two" style={{ marginTop: 12 }}>
              <SelectField testid="cheque-source-select" label="Portföydeki çek" value={sourceChequeId}
                           onChange={(e) => pickSourceCheque(e.target.value)} required>
                <option value="">— Ciro edilecek çeki seçin —</option>
                {portfolioCheques.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.check_no || "no-yok"} · {c.bank_name || "banka?"} · {c.customer_name || "cari?"} · {c.maturity_date} · {money(c.amount)}
                  </option>
                ))}
              </SelectField>
              <SelectField testid="cheque-endorse-to-customer" label="Ciro edilecek cari" value={form.customer_id}
                           onChange={(e) => pickCustomer(e.target.value)} required>
                <option value="">— Cari seçin —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SelectField>
            </div>
            {sourceChequeId ? (
              <div className="endorse-preview" data-testid="endorse-preview">
                <strong>Çek: {form.check_no}</strong>
                <div>{form.bank_name} {form.branch && `· ${form.branch}`}</div>
                <div>Tutar: <strong>{money(form.amount)}</strong> · Vade: {form.maturity_date}</div>
              </div>
            ) : <div className="line-empty">Önce portföyden bir çek seçin.</div>}
            <TextArea testid="cheque-endorse-note" label="Ciro notu" rows="2" value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="Örn. Şubat ödemesi için ciro" />
            <p className="modal-sub">
              Ciro işleminde orijinal çekin durumu <strong>"Ciro Edildi"</strong> olarak güncellenecek, seçilen cariye <strong>verilen çek</strong> olarak yeni kayıt açılacak.
            </p>
          </>
        )}

        {mode === "new" && (
          <>
            <div className="type-toggle" style={{ marginTop: 12 }}>
              <button data-testid="cheque-received" type="button"
                      className={form.direction === "received" ? "active positive" : ""}
                      onClick={() => setForm({ ...form, direction: "received" })}>Alınan</button>
              <button data-testid="cheque-given" type="button"
                      className={form.direction === "given" ? "active negative" : ""}
                      onClick={() => setForm({ ...form, direction: "given" })}>Verilen</button>
            </div>
            <div className="form-grid two">
              <Field testid="cheque-no-input" label="Çek numarası" value={form.check_no}
                     onChange={(e) => setForm({ ...form, check_no: e.target.value })} />
              <MoneyField testid="cheque-amount-input" label="Tutar (₺)" value={form.amount} required
                     onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <Field testid="cheque-bank-input" label="Banka" value={form.bank_name}
                     onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
              <Field testid="cheque-branch-input" label="Şube" value={form.branch}
                     onChange={(e) => setForm({ ...form, branch: e.target.value })} />
              <Field testid="cheque-issue-input" label="Keşide tarihi" type="date" value={form.issue_date}
                     onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
              <Field testid="cheque-maturity-input" label="Vade tarihi" type="date" value={form.maturity_date} required
                     onChange={(e) => setForm({ ...form, maturity_date: e.target.value })} />
              <SelectField testid="cheque-customer-select" label={<>Cari <CustomerInfoButton customerId={form.customer_id} customers={customers} testid="cheque-customer-info" /></>} value={form.customer_id}
                           onChange={(e) => pickCustomer(e.target.value)}>
                <option value="">— Cari yok —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </SelectField>
              <SelectField testid="cheque-status-select" label="Durum" value={form.status}
                           onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option>Portföyde</option>
                <option>Tahsil Edildi</option>
                <option>Karşılıksız</option>
                <option>Ödendi</option>
                <option>İade</option>
                <option>Ciro Edildi</option>
              </SelectField>
            </div>
            <TextArea testid="cheque-note-input" label="Not" rows="2" value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </>
        )}
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel={mode === "endorse" ? "Çeki ciro et" : "Kaydet"} />
      </form>
    </Modal>
  );
}

// ---------- User modal ----------
export function UserModal({ onClose, onSaved, availablePerms }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    permissions: { view_banks: true, view_cheques: true },
  });
  const [error, setError] = useState("");

  const togglePerm = (p) => setForm({ ...form, permissions: { ...form.permissions, [p]: !form.permissions[p] } });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Kullanıcı oluşturulamadı");
    }
  };

  return (
    <Modal title="Yeni kullanıcı" size="lg" onClose={onClose} testid="user-modal-form">
      <form onSubmit={submit}>
        <div className="form-grid two">
          <Field testid="user-name-input" label="Ad soyad" value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field testid="user-email-input" label="E-posta" type="email" value={form.email} required onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Field testid="user-password-input" label="Şifre" type="password" value={form.password} required minLength={6} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>

        <div className="perm-section">
          <div className="perm-head">
            <strong>Görüntüleme yetkileri</strong>
            <p>Kullanıcı kapatılmadığı sürece bu modülleri görebilir.</p>
          </div>
          <div className="perm-grid">
            {["view_banks", "view_cheques"].map((p) => (
              <label key={p} className="perm-check">
                <input data-testid={`perm-${p}`} type="checkbox" checked={!!form.permissions[p]} onChange={() => togglePerm(p)} />
                <div>
                  <strong>{PERM_LABELS[p]}</strong>
                  <small>{p === "view_banks" ? "Banka & kasa modülünü ve bakiyeleri görür." : "Alınan ve verilen çekleri görüntüleyebilir."}</small>
                </div>
              </label>
            ))}
          </div>
          <div className="perm-head">
            <strong>Panel erişimi</strong>
            <p>Kapalıysa kullanıcı yalnızca İş Takip paneline girebilir.</p>
          </div>
          <div className="perm-grid">
            <label className="perm-check">
              <input data-testid="perm-access_accounting" type="checkbox" checked={!!form.permissions.access_accounting} onChange={() => togglePerm("access_accounting")} />
              <div>
                <strong>{PERM_LABELS.access_accounting}</strong>
                <small>Muhasebe panelini açar; kapalıysa giriş İş Takip ile sınırlı kalır.</small>
              </div>
            </label>
          </div>
          <div className="perm-head">
            <strong>Silme & düzenleme yetkileri</strong>
            <p>Varsayılan olarak kapalı. "Geçmiş tarihli" seçenekleri kapalıysa kullanıcı sadece bugüne ait kayıtlarda işlem yapabilir.</p>
          </div>
          <div className="perm-grid">
            {availablePerms.filter((p) => !["view_banks", "view_cheques", "manage_users", "access_accounting"].includes(p)).map((p) => (
              <label key={p} className="perm-check">
                <input data-testid={`perm-${p}`} type="checkbox" checked={!!form.permissions[p]} onChange={() => togglePerm(p)} />
                <div>
                  <strong>{PERM_LABELS[p] || p}</strong>
                </div>
              </label>
            ))}
          </div>
          <div className="perm-head">
            <strong>Yönetici yetkisi</strong>
          </div>
          <div className="perm-grid">
            <label className="perm-check">
              <input data-testid="perm-manage_users" type="checkbox" checked={!!form.permissions.manage_users} onChange={() => togglePerm("manage_users")} />
              <div>
                <strong>{PERM_LABELS.manage_users}</strong>
                <small>Yeni kullanıcı ekleyebilir ve yetkilerini düzenler.</small>
              </div>
            </label>
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel="Kullanıcıyı oluştur" />
      </form>
    </Modal>
  );
}

// ---------- User edit (bilgiler, şifre, yetkiler) ----------
const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPRSTUVYZabcdefghjkmnprstuvyz23456789";
  return Array.from(crypto.getRandomValues(new Uint32Array(10)), (n) => chars[n % chars.length]).join("");
};

function SavedPassword({ user, email, password, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const text = `${email} / ${password}`;
    if (navigator.clipboard) return navigator.clipboard.writeText(text).then(() => setCopied(true)).catch(() => {});
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); setCopied(true); } finally { ta.remove(); }
  };
  return (
    <Modal title={`Şifre güncellendi · ${user.name}`} onClose={onClose} testid="user-password-result">
      <p className="muted-note">Bu şifre güvenlik nedeniyle sadece şimdi gösterilir. Kullanıcıya iletin.</p>
      <div className="password-reveal">
        <div><small>E-posta</small><strong data-testid="saved-email">{email}</strong></div>
        <div><small>Yeni şifre</small><strong data-testid="saved-password">{password}</strong></div>
      </div>
      <div className="modal-actions">
        <button data-testid="copy-password-button" type="button" className="secondary-button" onClick={copy}>
          {copied ? <Check size={15} /> : null} {copied ? "Kopyalandı" : "Kopyala"}
        </button>
        <button data-testid="close-password-result" type="button" className="primary-button" onClick={onClose}>Tamam</button>
      </div>
    </Modal>
  );
}

export function UserPermsModal({ user, me, onClose, onSaved, availablePerms }) {
  const isAdmin = user.role === "admin";
  const isSelf = me?.id === user.id;
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", password: "" });
  const [perms, setPerms] = useState({ ...user.permissions });
  const [allowedAcc, setAllowedAcc] = useState(user.allowed_accounts || []);
  const [allAccounts, setAllAccounts] = useState([]);
  useEffect(() => { if (!isAdmin) api.get("/accounts").then((r) => setAllAccounts(r.data)).catch(() => {}); }, [isAdmin]);
  const toggleAcc = (id) => setAllowedAcc(allowedAcc.includes(id) ? allowedAcc.filter((x) => x !== id) : [...allowedAcc, id]);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(null);
  const locked = (p) => isSelf && p === "manage_users" && !!user.permissions?.manage_users;
  const toggle = (p) => !locked(p) && setPerms({ ...perms, [p]: !perms[p] });
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const body = { name: form.name, email: form.email, ...(form.password ? { password: form.password } : {}) };
      if (!isAdmin) { body.permissions = perms; body.allowed_accounts = allowedAcc; }
      await api.put(`/users/${user.id}`, body);
      onSaved();
      if (form.password) setSaved({ email: form.email.toLowerCase().trim(), password: form.password });
      else onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Güncellenemedi");
    }
  };
  if (saved) return <SavedPassword user={user} {...saved} onClose={onClose} />;
  return (
    <Modal title={`Kullanıcıyı düzenle · ${user.name}`} size="lg" onClose={onClose} testid="user-perms-modal">
      <form onSubmit={submit}>
        <div className="form-grid two">
          <Field testid="edit-user-name" label="Ad soyad" value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field testid="edit-user-email" label="E-posta" type="email" value={form.email} required onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <label className="field">
            <span>Yeni şifre <small>(boş bırakılırsa değişmez)</small></span>
            <div className="password-row">
              <input data-testid="edit-user-password" type={showPw ? "text" : "password"} minLength={6} value={form.password}
                     placeholder="En az 6 karakter" onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <button type="button" className="secondary-button" data-testid="toggle-password-visibility" onClick={() => setShowPw(!showPw)}>
                {showPw ? "Gizle" : "Göster"}
              </button>
              <button type="button" className="secondary-button" data-testid="generate-password-button"
                      onClick={() => { setForm({ ...form, password: genPassword() }); setShowPw(true); }}>
                Oluştur
              </button>
            </div>
          </label>
        </div>
        {isAdmin ? (
          <p className="muted-note" data-testid="admin-perms-note">Yönetici hesabı tüm yetkilere sahiptir; yetkileri değiştirilemez.</p>
        ) : (
          <div className="perm-grid">
            {availablePerms.map((p) => (
              <label key={p} className="perm-check" title={locked(p) ? "Kendi kullanıcı yönetimi yetkinizi kaldıramazsınız" : ""}>
                <input data-testid={`edit-perm-${p}`} type="checkbox" checked={!!perms[p]} disabled={locked(p)} onChange={() => toggle(p)} />
                <div><strong>{PERM_LABELS[p] || p}</strong></div>
              </label>
            ))}
          </div>
        )}
        {!isAdmin && (
          <div data-testid="allowed-accounts-section" style={{ marginTop: 16 }}>
            <strong>Görebileceği kasa / banka hesapları</strong>
            <p className="muted-note" style={{ margin: "4px 0 8px" }}>
              İşaretlenmeyen hesapların bakiyesini ve hareketlerini göremez, o hesaplarla işlem yapamaz.
              {" "}
              <button type="button" style={{ background: "none", border: 0, padding: 0, color: "#17a9d8", cursor: "pointer", fontWeight: 600 }} data-testid="allowed-accounts-all" onClick={() => setAllowedAcc(allAccounts.map((a) => a.id))}>Tümünü seç</button>
              {" · "}
              <button type="button" style={{ background: "none", border: 0, padding: 0, color: "#17a9d8", cursor: "pointer", fontWeight: 600 }} data-testid="allowed-accounts-none" onClick={() => setAllowedAcc([])}>Hiçbiri</button>
            </p>
            <div className="perm-grid">
              {allAccounts.map((a) => (
                <label key={a.id} className="perm-check">
                  <input data-testid={`allowed-account-${a.id}`} type="checkbox" checked={allowedAcc.includes(a.id)} onChange={() => toggleAcc(a.id)} />
                  <div><strong>{a.name}</strong></div>
                </label>
              ))}
            </div>
          </div>
        )}
        {error && <div className="form-error" data-testid="edit-user-error">{error}</div>}
        <div className="modal-actions">
          <button data-testid="modal-cancel-button" type="button" className="secondary-button" onClick={onClose}>Vazgeç</button>
          <button data-testid="save-perms-button" className="primary-button" type="submit">
            Kaydet <ArrowUpRight size={15} />
          </button>
        </div>
      </form>
    </Modal>
  );
}


// ---------- Transfer modal ----------
export function TransferModal({ onClose, onSaved, accounts }) {
  const [form, setForm] = useState({
    from_account_id: accounts[0]?.id || "",
    to_account_id: accounts[1]?.id || "",
    amount: 0,
    date: today(),
    description: "",
  });
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (form.from_account_id === form.to_account_id) return setError("Aynı hesaba transfer yapılamaz");
    try {
      await api.post("/transfers", { ...form, amount: Number(form.amount) });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Transfer başarısız");
    }
  };
  return (
    <Modal title="Hesaplar arası transfer" onClose={onClose} testid="transfer-modal-form">
      <form onSubmit={submit}>
        <div className="form-grid two">
          <SelectField testid="transfer-from-select" label="Kaynak hesap" value={form.from_account_id}
                       onChange={(e) => setForm({ ...form, from_account_id: e.target.value })} required>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {money(a.balance)}</option>)}
          </SelectField>
          <SelectField testid="transfer-to-select" label="Hedef hesap" value={form.to_account_id}
                       onChange={(e) => setForm({ ...form, to_account_id: e.target.value })} required>
            {accounts.filter((a) => a.id !== form.from_account_id).map((a) => (
              <option key={a.id} value={a.id}>{a.name} · {money(a.balance)}</option>
            ))}
          </SelectField>
          <MoneyField testid="transfer-amount-input" label="Tutar (₺)"
                 value={form.amount} required onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <Field testid="transfer-date-input" label="Tarih" type="date"
                 value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <TextArea testid="transfer-description-input" label="Açıklama" rows="2"
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <HideDailyToggle form={form} setForm={setForm} testid="transfer-hide-daily" />
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel="Transferi yap" />
      </form>
    </Modal>
  );
}

// ---------- Customer statement modal ----------
export function StatementModal({ customerId, onClose, onOpenEntry }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [year, setYear] = useState("");

  useEffect(() => {
    api.get(`/customers/${customerId}/statement`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Ekstre alınamadı"));
  }, [customerId]);

  const kindLabel = { invoice: "Fatura", transaction: "Kasa/Banka", cheque: "Çek", opening: "Açılış", purchase: "Alım Faturası" };

  const yearFilter = (entries) => {
    if (!year) return entries;
    return entries.filter((e) => (e.date || "").startsWith(String(year)));
  };
  const now = new Date();
  const years = [now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <Modal title="Cari ekstresi" size="xl" onClose={onClose} testid="statement-modal">
      {error && <div className="form-error">{error}</div>}
      {!data && !error && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Yükleniyor…</div>}
      {data && (
        <>
          {data.totals.balance_mismatch && (
            <div className="merge-warn danger" data-testid="statement-balance-mismatch">
              <AlertTriangle size={16} />
              <span><b>Bakiye uyuşmazlığı!</b> Cari listesinde {money(data.totals.list_balance)}, ekstrede {money(data.totals.balance)} görünüyor.
                Bu durum işlem geçmişine kaydedildi; lütfen yöneticiye bildirin.</span>
            </div>
          )}
          <div className="view-grid">
            <div><span>Cari</span><strong data-testid="statement-customer-name">{data.customer.name}</strong></div>
            <div><span>Tip</span><strong>{data.customer.type === "corporate" ? "Kurumsal" : "Şahıs"}</strong></div>
            <div><span>Toplam borç</span><strong className="negative" data-testid="statement-total-debit">{money(data.totals.debit)}</strong></div>
            <div><span>Toplam alacak</span><strong className="positive" data-testid="statement-total-credit">{money(data.totals.credit)}</strong></div>
          </div>
          <div className="balance-split" data-testid="statement-split-balance">
            <div className="split-row">
              <span>Resmi bakiye</span>
              <strong data-testid="statement-resmi-balance" className={data.totals.resmi_balance > 0 ? "negative" : ""}>
                {money(data.totals.resmi_balance || 0)}
              </strong>
            </div>
            <div className="split-row proforma">
              <span>Proforma bakiye</span>
              <strong data-testid="statement-proforma-balance" className={data.totals.proforma_balance > 0 ? "negative" : ""}>
                {money(data.totals.proforma_balance || 0)}
              </strong>
            </div>
            <div className="split-row total">
              <span>Genel bakiye</span>
              <strong className={data.totals.balance >= 0 ? "negative" : "positive"}>
                {money(Math.abs(data.totals.balance))} {data.totals.balance >= 0 ? "borç" : "alacak"}
              </strong>
            </div>
          </div>
          <table className="view-table statement-table">
            <thead>
              <tr>
                <th>Tarih</th><th>Tür</th><th>Açıklama</th>
                <th className="amount">Borç</th><th className="amount">Alacak</th><th className="amount">Bakiye</th>
              </tr>
            </thead>
            <tbody>
              {yearFilter(data.entries).length === 0 && (
                <tr><td colSpan="6" className="empty">
                  {year ? `${year} yılında hareket yok.` : "Bu cariye ait hareket yok."}
                </td></tr>
              )}
              {yearFilter(data.entries).map((e) => {
                const [kind, rawId] = (e.id || "").split("-", 2);
                const refId = (e.id || "").substring(kind.length + 1); // uuid may contain '-'
                const lookupKey = kind === "inv" ? "invoices_lookup" : kind === "tx" ? "transactions_lookup" : kind === "chq" ? "cheques_lookup" : null;
                const linked = lookupKey && data[lookupKey] ? data[lookupKey][refId] : null;
                void rawId;
                const canOpen = !!linked && !!onOpenEntry;
                return (
                  <tr key={e.id} data-testid={`statement-row-${e.id}`}
                      className={canOpen ? "clickable-row" : ""}
                      onClick={canOpen ? (ev) => onOpenEntry(e.kind || (kind === "inv" ? "invoice" : kind === "tx" ? "transaction" : "cheque"), linked, ev) : undefined}
                      onAuxClick={canOpen ? (ev) => { if (ev.button === 1) onOpenEntry(e.kind || (kind === "inv" ? "invoice" : kind === "tx" ? "transaction" : "cheque"), linked, ev); } : undefined}>
                    <td>{e.date || "—"}</td>
                    <td>
                      <span className={`type-tag ${e.kind === "invoice" && e.invoice_type === "Proforma" ? "individual" : ""}`}>
                        {e.kind === "invoice" ? (e.invoice_type || "Resmi") : kindLabel[e.kind]}
                      </span>
                    </td>
                    <td><strong>{e.title}</strong><small>{e.detail}</small>
                      {(e.plates || e.work) && <small className="stmt-summary" data-testid={`stmt-summary-${e.id}`}>{[e.plates, e.work].filter(Boolean).join(" · ")}</small>}</td>
                    <td className="amount negative">{e.debit ? money(e.debit) : "—"}</td>
                    <td className="amount positive">{e.credit ? money(e.credit) : "—"}</td>
                    <td className={`amount ${e.balance >= 0 ? "negative" : "positive"}`}>{money(Math.abs(e.balance))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="modal-actions">
            <label className="field inline-field" style={{ marginRight: "auto" }}>
              <span>Yıl filtresi</span>
              <select data-testid="statement-year" value={year} onChange={(e) => setYear(e.target.value)}>
                <option value="">Tüm zamanlar</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <button type="button" className="secondary-button" onClick={onClose}>Kapat</button>
            <button data-testid="statement-excel-download" type="button" className="secondary-button"
                    onClick={() => download(`/customers/${customerId}/statement/export?fmt=xlsx`, `ekstre.xlsx`)}>
              <Download size={15} /> Excel
            </button>
            <button data-testid="statement-pdf-download" type="button" className="primary-button"
                    onClick={() => download(`/customers/${customerId}/statement/pdf${year ? `?year=${year}` : ""}`, `ekstre${year ? `-${year}` : ""}.pdf`)}>
              <Download size={15} /> PDF indir {year && `(${year})`}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}


// ---------- Bulk status modal for invoices ----------
export function BulkStatusModal({ ids, accounts, onClose, onSaved }) {
  const [status, setStatus] = useState("Ödendi");
  const [paidAccountId, setPaidAccountId] = useState(accounts[0]?.id || "");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (status === "Ödendi" && !paidAccountId) return setError("Ödeme hesabı seçin");
    try {
      await api.put("/invoices/bulk-status", {
        ids, status,
        paid_account_id: status === "Ödendi" ? paidAccountId : undefined,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Güncellenemedi");
    }
  };
  return (
    <Modal title={`Toplu durum · ${ids.length} fatura`} onClose={onClose} testid="bulk-status-modal">
      <form onSubmit={submit}>
        <div className="type-toggle">
          <button data-testid="bulk-status-odenecek" type="button"
                  className={status === "Ödenecek" ? "active" : ""}
                  onClick={() => setStatus("Ödenecek")}>Ödenecek (borç)</button>
          <button data-testid="bulk-status-odendi" type="button"
                  className={status === "Ödendi" ? "active positive" : ""}
                  onClick={() => setStatus("Ödendi")}>Ödendi (tahsil)</button>
        </div>
        {status === "Ödendi" && (
          <SelectField testid="bulk-paid-account" label="Ödeme hesabı" value={paidAccountId}
                       onChange={(e) => setPaidAccountId(e.target.value)}>
            <option value="">— Hesap seçin —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.type === "cash" ? "Kasa" : a.type === "pos" ? "POS" : a.bank_name || "Banka"}
              </option>
            ))}
          </SelectField>
        )}
        <p className="modal-sub" style={{ marginTop: 12 }}>
          Seçili {ids.length} fatura {status === "Ödendi" ? "Ödendi olarak işaretlenecek ve cari kartlarında tahsilat olarak görünecek." : "Ödenecek olarak işaretlenecek ve cari kartlarında borç olarak görünecek."}
        </p>
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel="Toplu güncelle" />
      </form>
    </Modal>
  );
}


// ---------- Customer combobox for invoice ----------
export function CustomerCombobox({ customers, selectedId, selectedName, onSelect, onCreateNew, label = "Cari", testid = "invoice-customer", onCustomerUpdated }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const norm = (s) => (s || "").toString().toLocaleLowerCase("tr");
  const filtered = useMemo(() => {
    const needle = norm(q);
    if (!needle) return customers.slice(0, 12);
    return customers.filter((c) => {
      const bag = [c.name, c.tc_no, c.tax_no, c.phone, c.city, c.district, c.email, c.tax_office]
        .map(norm).join(" ");
      return needle.split(/\s+/).every((w) => bag.includes(w));
    }).slice(0, 12);
  }, [customers, q]);

  useEffect(() => { setFocusIdx(0); }, [q]);

  const pick = (c) => {
    onSelect(c);
    setQ("");
    setOpen(false);
  };
  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, filtered.length)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (focusIdx < filtered.length) pick(filtered[focusIdx]);
      else if (onCreateNew) onCreateNew();
      setOpen(false);
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <label className="field customer-combo" ref={boxRef}>
      <span>{label} <small className="hint-inline">(ad, soyad, firma, TC, vergi no, telefon)</small>
        <CustomerInfoButton customerId={selectedId} customers={customers} testid={`${testid}-info`}
                            onSaved={(d) => { onCustomerUpdated?.(d); if (d?.name && d.name !== selectedName) onSelect(d); }} />
      </span>
      <div className="combo-input-wrap">
        <input
          data-testid={`${testid}-search`}
          value={open ? q : selectedName || q}
          placeholder={selectedName ? selectedName : "Cari ara..."}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
        {selectedId && !open && (
          <button type="button" className="combo-clear" onClick={() => { onSelect({ id: "", name: "" }); setQ(""); }} data-testid={`${testid}-clear`}>
            <X size={14} />
          </button>
        )}
      </div>
      {open && (
        <div className="combo-list" data-testid={`${testid}-list`}>
          {filtered.length === 0 && <div className="combo-empty">Eşleşen cari bulunamadı.</div>}
          {filtered.map((c, i) => (
            <button key={c.id} type="button"
                    data-testid={`combo-option-${c.id}`}
                    className={`combo-option ${i === focusIdx ? "focused" : ""}`}
                    onMouseEnter={() => setFocusIdx(i)}
                    onClick={() => pick(c)}>
              <div>
                <strong>{c.name}</strong>
                <small>
                  {c.type === "corporate" ? "Kurumsal" : "Şahıs"}
                  {(c.tc_no || c.tax_no) && ` · ${c.tc_no || c.tax_no}`}
                  {c.phone && ` · ${c.phone}`}
                  {c.city && ` · ${c.city}`}
                </small>
              </div>
              <span className={`type-tag ${c.type}`}>{c.type === "corporate" ? "K" : "Ş"}</span>
            </button>
          ))}
          {onCreateNew && <button type="button" data-testid="combo-create-new"
                  className={`combo-option combo-new ${focusIdx === filtered.length ? "focused" : ""}`}
                  onMouseEnter={() => setFocusIdx(filtered.length)}
                  onClick={() => { onCreateNew(); setOpen(false); }}>
            <Plus size={14} /> Yeni cari oluştur
          </button>}
        </div>
      )}
    </label>
  );
}

// ---------- Stock combobox (invoice line) ----------
export function StockCombobox({ stocks, selectedId, selectedLabel, onSelect, index }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const norm = (s) => (s || "").toString().toLocaleLowerCase("tr");
  const filtered = useMemo(() => {
    const needle = norm(q);
    if (!needle) return stocks.slice(0, 15);
    return stocks.filter((s) => {
      const bag = [s.code, s.name, s.description, s.unit, s.barcode].map(norm).join(" ");
      return needle.split(/\s+/).every((w) => bag.includes(w));
    }).slice(0, 15);
  }, [stocks, q]);

  useEffect(() => { setFocusIdx(0); }, [q]);

  // barcode scanner auto-select: exact barcode match + only one filtered result
  useEffect(() => {
    if (!q || q.length < 4) return;
    const exact = stocks.find((s) => s.barcode && norm(s.barcode) === norm(q));
    if (exact) {
      pick(exact);
    }
    // eslint-disable-next-line
  }, [q]);

  const pick = (s) => { onSelect(s); setQ(""); setOpen(false); };
  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[focusIdx]) pick(filtered[focusIdx]); }
    else if (e.key === "Escape") { setOpen(false); setQ(""); }
  };

  return (
    <div className="stock-combo" ref={boxRef} title={selectedLabel || ""}>
      <div className="combo-input-wrap">
        <input
          data-testid={`product-stock-search-${index}`}
          value={open ? q : (selectedLabel || "")}
          placeholder={selectedLabel || "Kayıtlı stoklardan seçin (kod / ad / barkod)"}
          title={selectedLabel || ""}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
      </div>
      {open && (
        <div className="combo-list stock-combo-list" data-testid={`product-stock-list-${index}`}>
          {selectedLabel && <div className="combo-fullname" data-testid={`product-stock-fullname-${index}`}><small>Seçili:</small> {selectedLabel}</div>}
          {filtered.length === 0 && <div className="combo-empty">Eşleşen stok bulunamadı. Sadece kayıtlı stoklar seçilebilir.</div>}
          {filtered.map((s, i) => (
            <button key={s.id} type="button"
                    data-testid={`stock-option-${index}-${s.id}`}
                    className={`combo-option ${i === focusIdx ? "focused" : ""}`}
                    onMouseEnter={() => setFocusIdx(i)}
                    onClick={() => pick(s)}>
              <div>
                <strong>{s.code} · {s.name}</strong>
                <small>
                  {s.barcode ? `Barkod: ${s.barcode} · ` : ""}
                  {s.description ? `${s.description} · ` : ""}
                  Stok: {s.quantity} {s.unit} · {money(s.price)} · KDV %{s.kdv ?? 20}
                </small>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Quick customer payment/collection modal ----------
export function QuickPaymentModal({ customer, customers = [], accounts, direction: initialDirection, onClose, onSaved }) {
  const [form, setForm] = useState({
    direction: initialDirection || "in",
    amount: 0,
    date: today(),
    account_id: accounts[0]?.id || "",
    proxy_customer_id: null,
    balance_target: "Resmi",
    description: "",
    commission: 0,
    method: "cash",
    cheque_mode: "portfolio",
    portfolio_id: "",
    check_no: "", bank_name: "", branch: "", maturity_date: addDays(today(), 30),
  });
  const [error, setError] = useState("");
  const [portfolio, setPortfolio] = useState([]);
  const isCheque = form.method === "cheque";
  useEffect(() => {
    if (!isCheque) return;
    api.get("/cheques").then((r) => setPortfolio(r.data.filter((c) => c.direction === "received" && c.status === "Portföyde" && c.customer_id !== customer.id)))
      .catch(() => setPortfolio([]));
  }, [isCheque, customer.id]);

  const submitCheque = async () => {
    const note = form.description;
    if (form.direction === "out" && form.cheque_mode === "portfolio") {
      if (!form.portfolio_id) return setError("Ciro edilecek çeki seçin.");
      await api.post(`/cheques/${form.portfolio_id}/endorse`, { customer_id: customer.id, date: form.date, note, balance_target: form.balance_target });
    } else {
      if (Number(form.amount) <= 0) return setError("Tutar sıfırdan büyük olmalı.");
      if (!form.maturity_date) return setError("Vade tarihini girin.");
      await api.post("/cheques", {
        direction: form.direction === "in" ? "received" : "given", customer_id: customer.id, customer_name: customer.name,
        amount: Number(form.amount), issue_date: form.date, maturity_date: form.maturity_date, check_no: form.check_no,
        bank_name: form.bank_name, branch: form.branch, note, balance_target: form.balance_target, status: "Portföyde",
      });
    }
    onSaved(); onClose();
  };

  const isProxy = !isCheque && form.proxy_customer_id !== null;
  const canCommission = !isCheque && !isProxy && !!form.account_id;
  const submit = async (e) => {
    e.preventDefault();
    if (isCheque) {
      try { return await submitCheque(); } catch (err) { return setError(err.response?.data?.detail || "Çek kaydedilemedi"); }
    }
    if (isProxy) {
      if (!form.proxy_customer_id) return setError("Cari Hesap seçin.");
    } else {
      if (!form.account_id) return setError("Bir banka/kasa hesabı seçin.");
    }
    if (Number(form.amount) <= 0) return setError("Tutar sıfırdan büyük olmalı.");
    if (Number(form.commission) < 0) return setError("Komisyon negatif olamaz.");
    try {
      await api.post("/transactions", {
        account_id: isProxy ? null : form.account_id,
        proxy_customer_id: isProxy ? form.proxy_customer_id : null,
        direction: form.direction,
        amount: Number(form.amount),
        date: form.date,
        category: form.direction === "in" ? "Tahsilat" : "Ödeme",
        description: form.description || `${form.balance_target} ${form.direction === "in" ? "tahsilat" : "ödeme"} · ${customer.name}`,
        customer_id: customer.id,
        balance_target: form.balance_target,
      });
      // Komisyon: ayrı bir "gider" hareketi — carinin bakiyesine dokunmaz
      const commission = Number(form.commission) || 0;
      if (commission > 0 && canCommission) {
        try {
          await api.post("/transactions", {
            account_id: form.account_id,
            direction: "out",
            amount: commission,
            date: form.date,
            category: "Komisyon",
            description: `Banka komisyonu · ${customer.name}${form.description ? " · " + form.description : ""}`,
            customer_id: null,
          });
        } catch (ex2) {
          // Ana hareket kaydedildi, komisyon kaydedilemedi: kullanıcıyı uyar, modali kapatma
          onSaved();
          setError("Ana hareket kaydedildi ancak banka komisyonu kaydedilemedi: " +
                   (ex2.response?.data?.detail || ex2.message || "bilinmeyen hata"));
          return;
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const proxyName = isProxy && form.proxy_customer_id
    ? (customers.find((c) => c.id === form.proxy_customer_id)?.name || "Seçili cari")
    : "";

  return (
    <Modal title={`Hızlı hareket · ${customer.name}`} onClose={onClose} testid="quick-payment-modal">
      <form onSubmit={submit}>
        <div className="type-toggle">
          <button data-testid="qp-in" type="button"
                  className={form.direction === "in" ? "active positive" : ""}
                  onClick={() => setForm({ ...form, direction: "in" })}>
            Tahsilat (Müşteriden Gelen)
          </button>
          <button data-testid="qp-out" type="button"
                  className={form.direction === "out" ? "active negative" : ""}
                  onClick={() => setForm({ ...form, direction: "out" })}>
            Ödeme (Müşteriye Verilen)
          </button>
        </div>
        <div className="type-toggle" style={{ marginTop: 12 }}>
          <button data-testid="qp-target-resmi" type="button"
                  className={form.balance_target === "Resmi" ? "active" : ""}
                  onClick={() => setForm({ ...form, balance_target: "Resmi" })}>
            Resmi bakiyeye işlensin
          </button>
          <button data-testid="qp-target-proforma" type="button"
                  className={form.balance_target === "Proforma" ? "active" : ""}
                  onClick={() => setForm({ ...form, balance_target: "Proforma" })}>
            Proforma bakiyeye işlensin
          </button>
        </div>
        <div className="type-toggle" style={{ marginTop: 12 }}>
          <button data-testid="qp-method-cash" type="button" className={!isCheque ? "active" : ""}
                  onClick={() => setForm({ ...form, method: "cash" })}>Nakit / Banka / Kart</button>
          <button data-testid="qp-method-cheque" type="button" className={isCheque ? "active" : ""}
                  onClick={() => setForm({ ...form, method: "cheque" })}>{form.direction === "in" ? "Çek tahsilatı" : "Çek ile ödeme"}</button>
        </div>
        {isCheque && form.direction === "out" && (
          <div className="type-toggle" style={{ marginTop: 10 }}>
            <button data-testid="qp-cheque-portfolio" type="button" className={form.cheque_mode === "portfolio" ? "active" : ""}
                    onClick={() => setForm({ ...form, cheque_mode: "portfolio" })}>Portföyden ciro et</button>
            <button data-testid="qp-cheque-own" type="button" className={form.cheque_mode === "own" ? "active" : ""}
                    onClick={() => setForm({ ...form, cheque_mode: "own" })}>Kendi çekimiz</button>
          </div>
        )}
        {isCheque && form.direction === "out" && form.cheque_mode === "portfolio" ? (
          <div className="form-grid two" style={{ marginTop: 14 }}>
            <SelectField testid="qp-portfolio-select" label="Portföydeki müşteri çeki" value={form.portfolio_id}
                         onChange={(e) => setForm({ ...form, portfolio_id: e.target.value })}>
              <option value="">— Çek seçin ({portfolio.length} adet portföyde) —</option>
              {portfolio.map((c) => (
                <option key={c.id} value={c.id}>{`${c.check_no || "—"} · ${c.customer_name} · vade ${humanDate(c.maturity_date)} · ${money(c.amount)}`}</option>
              ))}
            </SelectField>
            <Field testid="qp-date-input" label="Ciro tarihi" type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
        ) : isCheque ? (
          <div className="form-grid two" style={{ marginTop: 14 }} data-testid="qp-cheque-fields">
            <Field testid="qp-cheque-no" label="Çek no" value={form.check_no} onChange={(e) => setForm({ ...form, check_no: e.target.value.toUpperCase() })} />
            <MoneyField testid="qp-amount-input" label="Tutar (₺)" value={form.amount} required onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            <Field testid="qp-cheque-bank" label="Banka" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
            <Field testid="qp-cheque-branch" label="Şube" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} />
            <Field testid="qp-date-input" label={form.direction === "in" ? "Alış tarihi" : "Keşide tarihi"} type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <Field testid="qp-cheque-maturity" label="Vade tarihi" type="date" value={form.maturity_date}
                   onChange={(e) => setForm({ ...form, maturity_date: e.target.value })} />
          </div>
        ) : (
        <div className="form-grid two" style={{ marginTop: 14 }}>
          <SelectField testid="qp-account-select" label="Banka / Kasa"
                       value={isProxy ? "__proxy__" : form.account_id}
                       onChange={(e) => {
                         const v = e.target.value;
                         if (v === "__proxy__") setForm({ ...form, account_id: "", proxy_customer_id: "" });
                         else setForm({ ...form, account_id: v, proxy_customer_id: null });
                       }} required>
            <option value="">— Hesap seçin —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.type === "cash" ? "Kasa" : a.bank_name || "Banka"}
              </option>
            ))}
            <option value="__proxy__" data-testid="qp-account-option-proxy">Cari Hesap (başka bir carinin kartı/hesabı)</option>
          </SelectField>
          <MoneyField testid="qp-amount-input" label="Tutar (₺)" value={form.amount} required
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <Field testid="qp-date-input" label="Tarih" type="date" value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        )}
        {isProxy && (
          <div className="proxy-customer-section" data-testid="qp-proxy-section" style={{ marginTop: 12 }}>
            <SelectField testid="qp-proxy-customer-select"
                         label={form.direction === "in"
                           ? "Tahsilatın düşüleceği cari (borçlu olduğunuz firma)"
                           : "Ödemenin yapılacağı cari (sizin borçlu olduğunuz firma)"}
                         value={form.proxy_customer_id || ""}
                         onChange={(e) => setForm({ ...form, proxy_customer_id: e.target.value })}>
              <option value="">— Cari seçin —</option>
              {customers.filter((c) => c.id !== customer.id).map((c) => {
                const bal = Number(c.balance || 0);
                const label = bal < 0
                  ? `${c.name} · Alacağı: ${Math.abs(bal).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`
                  : `${c.name} · Bakiye: ${bal.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`;
                return <option key={c.id} value={c.id}>{label}</option>;
              })}
            </SelectField>
          </div>
        )}
        <TextArea testid="qp-description-input" label="Açıklama" rows="2"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Örn. Ocak ayı ödemesi" />
        {canCommission && (
          <div className="form-grid two" style={{ marginTop: 6 }}>
            <MoneyField testid="qp-commission-input" label="Banka komisyonu (₺) — opsiyonel"
                   value={form.commission}
                   onChange={(e) => setForm({ ...form, commission: e.target.value })}
                   placeholder="0,00" />
            <div className="muted" style={{ fontSize: 11.5, alignSelf: "flex-end", paddingBottom: 10 }} data-testid="qp-commission-hint">
              Bu tutar müşteri bakiyesine yansımaz; sadece seçili bankadan "Komisyon" gideri olarak düşer.
            </div>
          </div>
        )}
        <p className="modal-sub" style={{ marginTop: 6 }}>
          {isProxy
            ? (form.direction === "in"
                ? `${customer.name} carisinden tahsilat kaydedilecek, ${form.balance_target} bakiyesi azalacak. Aynı anda ${proxyName} ile olan hesaptan da bu tutar düşülecek.`
                : `${customer.name} carisine ödeme kaydedilecek, ${form.balance_target} bakiyesi artacak. Aynı anda ${proxyName} ile olan hesap da bu tutar kadar artacak.`)
            : (form.direction === "in"
                ? `${customer.name} carisinden tahsilat kaydedilecek, ${isCheque ? "çek portföye eklenecek (kasaya tahsil edilince işlenir)" : "seçilen hesaba giriş yazılacak"} ve ${form.balance_target} bakiyesi azalacak.`
                : `${customer.name} carisine ödeme kaydedilecek, ${isCheque ? "çek verilen çekler listesine eklenecek" : "seçilen hesaptan çıkış yazılacak"} ve ${form.balance_target} bakiyesi artacak.`)}
        </p>
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel={form.direction === "in" ? "Tahsilatı kaydet" : "Ödemeyi kaydet"} />
      </form>
    </Modal>
  );
}

// ---------- Stock movements modal ----------
export function StockMovementsModal({ stockId, onClose, onOpenInvoice, onOpenPriceHistory }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/stocks/${stockId}/movements`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Hareketler alınamadı"));
  }, [stockId]);

  return (
    <Modal title={data ? `${data.stock.code} · ${data.stock.name}` : "Stok hareketleri"} size="xl" onClose={onClose} testid="stock-movements-modal">
      {error && <div className="form-error">{error}</div>}
      {!data && !error && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Yükleniyor…</div>}
      {data && (
        <>
          <div className="view-grid">
            <div><span>Stok kodu</span><strong>{data.stock.code}</strong></div>
            <div><span>Barkod</span><strong>{data.stock.barcode || "—"}</strong></div>
            <div><span>Kalan stok</span><strong>{data.stock.quantity} {data.stock.unit}</strong></div>
            <div><span>Birim fiyat</span><strong>{money(data.stock.price)}</strong></div>
            <div><span>Toplam satış</span><strong data-testid="stock-mov-total-qty">{data.totals.total_quantity} {data.stock.unit}</strong></div>
            <div><span>Bugün satılan</span><strong data-testid="stock-mov-today-qty" className={data.totals.today_quantity > 0 ? "positive" : ""}>{data.totals.today_quantity} {data.stock.unit}</strong></div>
            <div><span>Fatura sayısı</span><strong>{data.totals.invoice_count}</strong></div>
            <div><span>Toplam tutar</span><strong>{money(data.totals.total_amount)}</strong></div>
          </div>
          {data.stock.description && <p className="view-note" style={{marginTop:8}}>Açıklama: {data.stock.description}</p>}
          <table className="view-table statement-table" data-testid="stock-mov-table">
            <thead>
              <tr>
                <th>Tarih</th><th>Fatura</th><th>Cari</th>
                <th>Plaka / Şase</th>
                <th className="amount">Miktar</th>
                <th className="amount">Birim ₺</th>
                <th className="amount">Toplam</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.movements.length === 0 && (
                <tr><td colSpan="8" className="empty">Bu stok için hareket bulunamadı.</td></tr>
              )}
              {data.movements.map((m, i) => (
                <tr key={`${m.invoice_id}-${i}`} data-testid={`stock-mov-row-${m.invoice_id}`}>
                  <td>{m.date || "—"}</td>
                  <td>
                    <strong>{m.invoice_no}</strong>
                    <small>{m.invoice_type} · {m.invoice_status}</small>
                  </td>
                  <td>{m.customer_name || "—"}</td>
                  <td>
                    {m.plate || "—"}
                    {m.chassis_no && <small>{m.chassis_no}</small>}
                  </td>
                  <td className="amount">{m.quantity} {data.stock.unit}</td>
                  <td className="amount">{money(m.unit_price)}</td>
                  <td className="amount"><strong>{money(m.line_total)}</strong></td>
                  <td className="amount">
                    <button data-testid={`stock-mov-goto-${m.invoice_id}`} className="row-icon"
                            title="Faturayı aç" onClick={() => onOpenInvoice(m.invoice_id)}>
                      <ArrowUpRight size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={onClose}>Kapat</button>
            <button data-testid="stock-mov-price-history" type="button" className="secondary-button"
                    onClick={() => onOpenPriceHistory && onOpenPriceHistory(stockId)}>
              Fiyat geçmişi
            </button>
            <button data-testid="stock-mov-excel" type="button" className="primary-button"
                    onClick={() => download(`/stocks/${stockId}/movements/export?fmt=xlsx`, `stok-hareket-${data.stock.code}.xlsx`)}>
              <Download size={15} /> Excel indir
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------- Service modal ----------
export function ServiceModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ code: "", name: "", description: "", unit: "Adet", price: 0, kdv: 20, category: "Genel" });
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/services", { ...form, price: Number(form.price) });
      onSaved(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <Modal title="Hizmet kartı" onClose={onClose} testid="service-modal-form">
      <form onSubmit={submit}>
        <div className="form-grid two">
          <Field testid="service-code-input" label="Kod" value={form.code} required
                 onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="HZM-001" />
          <Field testid="service-name-input" label="Hizmet adı" value={form.name} required
                 onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Yağ değişimi" />
          <Field testid="service-category-input" label="Kategori" value={form.category}
                 onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Bakım, Onarım, Danışmanlık" />
          <SelectField testid="service-unit-select" label="Birim" value={form.unit}
                       onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            <option>Adet</option><option>Saat</option><option>Km</option><option>Gün</option><option>Sefer</option>
          </SelectField>
          <MoneyField testid="service-price-input" label="Birim ücret (₺)" value={form.price}
                 onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <SelectField testid="service-kdv-select" label="KDV oranı" value={form.kdv}
                       onChange={(e) => setForm({ ...form, kdv: Number(e.target.value) })}>
            {KDV_OPTIONS.map((k) => <option key={k} value={k}>%{k}</option>)}
          </SelectField>
        </div>
        <TextArea testid="service-description-input" label="Açıklama" rows="2" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} />
      </form>
    </Modal>
  );
}

// ---------- Service combobox for invoice line ----------
export function ServiceCombobox({ services, selectedLabel, onSelect, index }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(0);
  const boxRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const norm = (s) => (s || "").toString().toLocaleLowerCase("tr");
  const filtered = useMemo(() => {
    const needle = norm(q);
    if (!needle) return services.slice(0, 15);
    return services.filter((s) => {
      const bag = [s.code, s.name, s.description, s.category, s.unit].map(norm).join(" ");
      return needle.split(/\s+/).every((w) => bag.includes(w));
    }).slice(0, 15);
  }, [services, q]);
  useEffect(() => { setFocusIdx(0); }, [q]);
  const pick = (s) => { onSelect(s); setQ(""); setOpen(false); };
  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[focusIdx]) pick(filtered[focusIdx]); }
    else if (e.key === "Escape") { setOpen(false); setQ(""); }
  };
  return (
    <div className="stock-combo" ref={boxRef} title={selectedLabel || ""}>
      <div className="combo-input-wrap">
        <input data-testid={`service-search-${index}`}
               value={open ? q : (selectedLabel || "")}
               placeholder={selectedLabel || "Kayıtlı hizmetlerden seçin (kod / ad)"}
               title={selectedLabel || ""}
               onChange={(e) => { setQ(e.target.value); setOpen(true); }}
               onFocus={() => setOpen(true)}
               onKeyDown={onKeyDown}
               autoComplete="off" />
      </div>
      {open && (
        <div className="combo-list stock-combo-list" data-testid={`service-list-${index}`}>
          {selectedLabel && <div className="combo-fullname" data-testid={`service-fullname-${index}`}><small>Seçili:</small> {selectedLabel}</div>}
          {filtered.length === 0 && <div className="combo-empty">Eşleşen hizmet bulunamadı. Sadece kayıtlı hizmetler seçilebilir.</div>}
          {filtered.map((s, i) => (
            <button key={s.id} type="button"
                    data-testid={`service-option-${index}-${s.id}`}
                    className={`combo-option ${i === focusIdx ? "focused" : ""}`}
                    onMouseEnter={() => setFocusIdx(i)}
                    onClick={() => pick(s)}>
              <div>
                <strong>{s.code} · {s.name}</strong>
                <small>
                  {s.category ? `${s.category} · ` : ""}
                  {s.description ? `${s.description} · ` : ""}
                  {s.unit} · {money(s.price)} · KDV %{s.kdv ?? 20}
                </small>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- User activity modal ----------
export function UserActivityModal({ userId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({ invoices: new Set(), records: new Set(), transactions: new Set(), cheques: new Set() });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/users/${userId}/activity`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Aktivite alınamadı"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const toggle = (kind, id) => setSelected((s) => {
    const n = new Set(s[kind]);
    n.has(id) ? n.delete(id) : n.add(id);
    return { ...s, [kind]: n };
  });
  const toggleAll = (kind) => setSelected((s) => {
    const all = data[kind].map((x) => x.id);
    const cur = s[kind];
    const allChecked = all.every((id) => cur.has(id));
    return { ...s, [kind]: new Set(allChecked ? [] : all) };
  });
  const totalSelected = Object.values(selected).reduce((n, s) => n + s.size, 0);
  const bulkDelete = async () => {
    if (!totalSelected) return;
    if (!window.confirm(`${totalSelected} kaydı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    setBusy(true);
    try {
      const body = {
        entities: {
          invoices: Array.from(selected.invoices),
          records: Array.from(selected.records),
          transactions: Array.from(selected.transactions),
          cheques: Array.from(selected.cheques),
        },
      };
      const r = await api.post(`/users/${userId}/bulk-delete`, body);
      alert(`Silinen kayıt: ${r.data.total}`);
      setSelected({ invoices: new Set(), records: new Set(), transactions: new Set(), cheques: new Set() });
      load();
      onSaved && onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || "Toplu silme başarısız");
    } finally { setBusy(false); }
  };

  const section = (kind, label, rows, columns, renderCells) => (
    <div className="user-act-section" data-testid={`user-act-section-${kind}`}>
      <div className="user-act-head">
        <strong>{label} <span className="badge tone-neutral">{rows.length}</span></strong>
        {rows.length > 0 && (
          <button type="button" className="text-button" data-testid={`user-act-toggle-all-${kind}`}
                  onClick={() => toggleAll(kind)}>
            {rows.every((r) => selected[kind].has(r.id)) ? "Seçimi kaldır" : "Tümünü seç"}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="line-empty">Bu türde kayıt yok.</div>
      ) : (
        <table className="view-table">
          <thead>
            <tr><th style={{width:32}}></th>{columns.map((c) => <th key={c.label} className={c.align === "right" ? "amount" : ""}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r) => (
              <tr key={r.id} data-testid={`user-act-${kind}-${r.id}`}>
                <td>
                  <input type="checkbox" data-testid={`user-act-check-${kind}-${r.id}`}
                         checked={selected[kind].has(r.id)} onChange={() => toggle(kind, r.id)} />
                </td>
                {renderCells(r)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <Modal title={data ? `İşlem geçmişi · ${data.user.name}` : "İşlem geçmişi"} size="xl" onClose={onClose} testid="user-activity-modal">
      {error && <div className="form-error">{error}</div>}
      {!data && !error && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Yükleniyor…</div>}
      {data && (
        <>
          <div className="view-grid">
            <div><span>Kullanıcı</span><strong>{data.user.name}</strong></div>
            <div><span>E-posta</span><strong>{data.user.email}</strong></div>
            <div><span>Fatura</span><strong data-testid="user-act-count-invoices">{data.totals.invoices}</strong></div>
            <div><span>Gelir/Gider</span><strong data-testid="user-act-count-records">{data.totals.records}</strong></div>
            <div><span>Hareket</span><strong data-testid="user-act-count-transactions">{data.totals.transactions}</strong></div>
            <div><span>Çek</span><strong data-testid="user-act-count-cheques">{data.totals.cheques}</strong></div>
            <div><span>Audit log</span><strong>{data.totals.activity_log}</strong></div>
          </div>
          {section("invoices", "Faturalar", data.invoices,
            [{ label: "Fatura no" }, { label: "Cari" }, { label: "Tarih" }, { label: "Tutar", align: "right" }, { label: "Durum" }],
            (r) => (<><td><strong>{r.invoice_no}</strong><small>{r.invoice_type}</small></td>
                    <td>{r.customer_name}</td><td>{r.date}</td>
                    <td className="amount">{money(r.total)}</td><td>{r.status}</td></>))}
          {section("records", "Gelir / Gider", data.records,
            [{ label: "Başlık" }, { label: "Tür" }, { label: "Tarih" }, { label: "Tutar", align: "right" }],
            (r) => (<><td>{r.title}</td><td>{r.type === "income" ? "Gelir" : "Gider"}</td>
                    <td>{r.date}</td><td className="amount">{money(r.amount)}</td></>))}
          {section("transactions", "Banka & Kasa hareketleri", data.transactions,
            [{ label: "Kategori" }, { label: "Yön" }, { label: "Tarih" }, { label: "Tutar", align: "right" }],
            (r) => (<><td>{r.description || r.category}</td>
                    <td>{r.direction === "in" ? "Giriş" : "Çıkış"}</td>
                    <td>{r.date}</td><td className="amount">{money(r.amount)}</td></>))}
          {section("cheques", "Çekler", data.cheques,
            [{ label: "Çek no" }, { label: "Cari" }, { label: "Yön" }, { label: "Vade" }, { label: "Tutar", align: "right" }],
            (r) => (<><td>{r.check_no}</td><td>{r.customer_name}</td>
                    <td>{r.direction === "received" ? "Alınan" : "Verilen"}</td>
                    <td>{r.maturity_date}</td><td className="amount">{money(r.amount)}</td></>))}
          <div className="modal-actions">
            <span style={{marginRight: "auto", color: "var(--muted)", fontSize: 12}}>
              Seçili: <strong data-testid="user-act-selected-count">{totalSelected}</strong>
            </span>
            <button type="button" className="secondary-button" onClick={onClose}>Kapat</button>
            <button type="button" className="primary-button danger-btn"
                    data-testid="user-act-bulk-delete"
                    disabled={busy || totalSelected === 0} onClick={bulkDelete}>
              <Trash2 size={14} /> Seçileni sil ({totalSelected})
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------- Note modal (admin creates task) ----------
export function NoteModal({ onClose, onSaved, users = [] }) {
  const [form, setForm] = useState({ title: "", body: "", priority: "normal", due_date: "", assignees: [] });
  const [error, setError] = useState("");
  const nonAdmins = users.filter((u) => u.role !== "admin");
  const toggleAssignee = (id) => setForm((f) => ({
    ...f, assignees: f.assignees.includes(id) ? f.assignees.filter((x) => x !== id) : [...f.assignees, id],
  }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notes", {
        ...form,
        due_date: form.due_date || null,
      });
      onSaved(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <Modal title="Yeni not / iş" onClose={onClose} testid="note-modal-form">
      <form onSubmit={submit}>
        <Field testid="note-title-input" label="Başlık" value={form.title} required
               onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn. Şubat cari bakiyelerini kontrol et" />
        <TextArea testid="note-body-input" label="Açıklama / detay" rows="3" value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })} />
        <div className="form-grid two">
          <SelectField testid="note-priority-select" label="Öncelik" value={form.priority}
                       onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            <option value="low">Düşük</option>
            <option value="normal">Normal</option>
            <option value="high">Yüksek</option>
          </SelectField>
          <Field testid="note-due-input" label="Son tarih (opsiyonel)" type="date" value={form.due_date}
                 onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
        </div>
        <label className="field">
          <span>Atanan kullanıcılar (boş = herkese açık)</span>
          <div className="assignee-list" data-testid="note-assignees">
            {nonAdmins.length === 0 && <small style={{color:"#94a3b8"}}>Standart kullanıcı yok.</small>}
            {nonAdmins.map((u) => (
              <label key={u.id} className={form.assignees.includes(u.id) ? "assignee active" : "assignee"}>
                <input type="checkbox" data-testid={`note-assignee-${u.id}`}
                       checked={form.assignees.includes(u.id)} onChange={() => toggleAssignee(u.id)} />
                {u.name}
              </label>
            ))}
          </div>
        </label>
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel="Notu ekle" />
      </form>
    </Modal>
  );
}

// ---------- Stock price history modal ----------
export function CustomerTransferModal({ onClose, onSaved, customers = [], fromCustomerId = null }) {
  const activeCariler = useMemo(() => (customers || []).filter((c) => c.is_cari !== false), [customers]);
  const [form, setForm] = useState({
    from_customer_id: fromCustomerId || "",
    to_customer_id: "",
    amount: "",
    date: new Date().toISOString().slice(0, 10),
    balance_target: "",
    note: "",
  });
  const [error, setError] = useState("");
  const [mode, setMode] = useState("items");
  const [stmt, setStmt] = useState(null);
  const [picked, setPicked] = useState(new Set());
  useEffect(() => {
    setPicked(new Set());
    setStmt(null);
    if (mode === "items" && form.from_customer_id)
      api.get(`/customers/${form.from_customer_id}/statement`).then((r) => setStmt(r.data)).catch(() => setStmt({ entries: [] }));
  }, [mode, form.from_customer_id]);
  const pool = mode === "items" ? customers : activeCariler;
  const togglePick = (id) => { setError(""); setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (!form.from_customer_id || !form.to_customer_id)
        throw new Error("Her iki cariyi de seçin");
      if (mode === "items") {
        const ids = { inv: [], pur: [], tx: [], chq: [] };
        picked.forEach((pid) => { const [k, ...rest] = pid.split("-"); ids[k]?.push(rest.join("-")); });
        if (!picked.size) throw new Error("Aktarılacak kalemleri işaretleyin");
        const { data } = await api.post("/transfers/customer-items", {
          from_customer_id: form.from_customer_id, to_customer_id: form.to_customer_id,
          invoice_ids: ids.inv, purchase_ids: ids.pur, transaction_ids: ids.tx, cheque_ids: ids.chq,
        });
        alert(`${data.moved} kalem hedef cariye aktarıldı.`);
        onSaved?.();
        onClose();
        return;
      }
      await api.post("/transfers/customer", {
        from_customer_id: form.from_customer_id,
        to_customer_id: form.to_customer_id,
        amount: Number(form.amount),
        date: form.date,
        balance_target: form.balance_target,
        note: form.note,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Aktarım yapılamadı");
    }
  };
  return (
    <Modal title="Cari hareket aktarımı" size="lg" onClose={onClose} testid="customer-transfer-modal">
      <form onSubmit={submit}>
        <div className="tab-group" style={{ marginBottom: 12 }}>
          <button type="button" data-testid="transfer-mode-items" className={mode === "items" ? "tab active" : "tab"} onClick={() => setMode("items")}>Kalem aktar (fatura, ödeme, çek)</button>
          <button type="button" data-testid="transfer-mode-amount" className={mode === "amount" ? "tab active" : "tab"} onClick={() => setMode("amount")}>Tutar aktar</button>
        </div>
        <p style={{ margin: "0 0 12px", fontSize: 11, color: "var(--muted)" }}>
          {mode === "items"
            ? "Kaynak carinin seçtiğiniz faturaları, ödemeleri ve çekleri hedef cariye taşınır; iki carinin bakiyesi buna göre değişir."
            : "Bir cariden başka cariye bakiye aktarılır (ör. ciro, mahsup). Kaynak carinin alacağı azalır, hedef carinin alacağı artar."}
        </p>
        <div className="form-grid two">
          <CustomerCombobox testid="transfer-from" label="Kaynak cari" customers={pool}
                            selectedId={form.from_customer_id}
                            selectedName={pool.find((c) => c.id === form.from_customer_id)?.name || ""}
                            onSelect={(c) => setForm({ ...form, from_customer_id: c.id })} />
          <CustomerCombobox testid="transfer-to" label="Hedef cari" customers={pool.filter((c) => c.id !== form.from_customer_id)}
                            selectedId={form.to_customer_id}
                            selectedName={pool.find((c) => c.id === form.to_customer_id)?.name || ""}
                            onSelect={(c) => setForm({ ...form, to_customer_id: c.id })} />
        </div>
        {mode === "items" && (
          <div className="transfer-items" data-testid="transfer-items">
            {!form.from_customer_id ? <p className="muted">Önce kaynak cariyi seçin.</p> : !stmt ? <p className="muted">Yükleniyor…</p> : (
              <table className="view-table">
                <thead><tr><th style={{ width: 28 }}></th><th>Tarih</th><th>Kalem</th><th className="amount">Borç</th><th className="amount">Alacak</th></tr></thead>
                <tbody>
                  {stmt.entries.filter((x) => x.kind !== "opening").length === 0 && <tr><td colSpan="5" className="empty">Bu carinin aktarılacak kalemi yok.</td></tr>}
                  {stmt.entries.filter((x) => x.kind !== "opening").map((x) => (
                    <tr key={x.id} className="clickable-row" onClick={() => togglePick(x.id)}>
                      <td><input type="checkbox" data-testid={`transfer-item-${x.id}`} checked={picked.has(x.id)} onChange={() => togglePick(x.id)} onClick={(ev) => ev.stopPropagation()} /></td>
                      <td>{humanDate(x.date)}</td>
                      <td><strong>{x.title}</strong><small>{[x.detail, x.plates, x.work].filter(Boolean).join(" · ")}</small></td>
                      <td className="amount">{x.debit ? money(x.debit) : "—"}</td>
                      <td className="amount">{x.credit ? money(x.credit) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {picked.size > 0 && <p data-testid="transfer-items-count"><strong>{picked.size} kalem seçildi</strong></p>}
          </div>
        )}
        {mode === "amount" && <>
        <div className="form-grid two">
          <MoneyField testid="transfer-amount" label="Tutar" value={form.amount}
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
          <Field testid="transfer-date" label="Tarih" type="date" value={form.date}
                 onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <SelectField testid="transfer-balance-target" label="Hangi bakiyeye yazılsın?"
                       value={form.balance_target}
                       onChange={(e) => setForm({ ...form, balance_target: e.target.value })}>
            <option value="">Ana bakiye</option>
            <option value="Resmi">Resmi bakiye</option>
            <option value="Proforma">Proforma bakiye</option>
          </SelectField>
        </div>
        <TextArea testid="transfer-note" label="Not" rows="2" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Örn: ciro karşılığı, mahsup..." />
        </>}
        {error && <div className="form-error">{error}</div>}
        <ModalActions onCancel={onClose} submitLabel={mode === "items" ? "Seçilenleri aktar" : "Aktarımı yap"} />
      </form>
    </Modal>
  );
}

export function StockPriceHistoryModal({ stockId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/stocks/${stockId}/price-history`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Geçmiş alınamadı"));
  }, [stockId]);
  return (
    <Modal title={data ? `Fiyat geçmişi · ${data.stock.code} ${data.stock.name}` : "Fiyat geçmişi"}
           size="lg" onClose={onClose} testid="stock-price-history-modal">
      {error && <div className="form-error">{error}</div>}
      {!data && !error && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Yükleniyor…</div>}
      {data && (
        <>
          <div className="view-grid" style={{marginBottom:12}}>
            <div><span>Şu anki fiyat</span><strong data-testid="stock-price-current">{money(data.current_price)}</strong></div>
            <div><span>Değişiklik sayısı</span><strong>{data.history.length}</strong></div>
          </div>
          <table className="view-table">
            <thead><tr><th>Tarih</th><th>Değişen</th><th className="amount">Eski fiyat</th><th className="amount">Yeni fiyat</th><th className="amount">Fark</th></tr></thead>
            <tbody>
              {data.history.length === 0 ? (
                <tr><td colSpan="5" className="empty">Fiyat henüz değiştirilmemiş.</td></tr>
              ) : data.history.map((h, i) => {
                const diff = Number(h.new_price) - Number(h.old_price);
                const pct = h.old_price ? (diff / h.old_price * 100) : 0;
                return (
                  <tr key={i} data-testid={`price-history-row-${i}`}>
                    <td>{humanDate(h.date)}</td>
                    <td>{h.changed_by_name || "-"}</td>
                    <td className="amount">{money(h.old_price)}</td>
                    <td className="amount"><strong>{money(h.new_price)}</strong></td>
                    <td className={`amount ${diff >= 0 ? "positive" : "negative"}`}>
                      {diff >= 0 ? "+" : ""}{money(diff)} ({pct.toFixed(1)}%)
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="modal-actions">
            <button type="button" className="secondary-button" onClick={onClose}>Kapat</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------- Settings modal (admin) ----------
function BackupSection() {
  const [file, setFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const restore = async () => {
    setBusy(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("confirm", confirmText);
      const { data } = await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const c = data.counts || {};
      setMsg(`Yedek yüklendi: ${c.customers || 0} cari, ${c.invoices || 0} fatura, ${c.stocks || 0} stok, ${c.transactions || 0} hareket. Sayfa yenileniyor…`);
      setTimeout(() => window.location.reload(), 2500);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Yedek yüklenemedi");
    } finally { setBusy(false); }
  };
  return (
    <div className="setting-section backup-section" data-testid="backup-section">
      <div className="setting-label">
        <strong>Yedek indir / yükle</strong>
        <small>Kullanıcılar hariç tüm verileri tek dosyada indirin; başka bir kurulumda (örn. yayındaki site) yükleyin.</small>
      </div>
      <div className="button-row">
        <button type="button" className="secondary-button" data-testid="backup-download"
                onClick={() => download("/admin/backup", `serhan-yedek-${today()}.json.gz`)}>
          <Download size={14} /> Yedeği indir
        </button>
        <button type="button" className="secondary-button" data-testid="backup-excel-download"
                onClick={() => download("/admin/backup-excel", `serhan-excel-yedek-${today()}.zip`)}>
          <FileSpreadsheet size={14} /> Excel yedeği (ZIP)
        </button>
      </div>
      <div className="backup-restore">
        <label className="secondary-button file-pick" data-testid="backup-file-label">
          <input type="file" accept=".gz,application/gzip" data-testid="backup-file-input" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {file ? `Seçilen dosya: ${file.name}` : "Yedek dosyası seç (.json.gz)"}
        </label>
        <Field testid="backup-confirm-input" label='Onay için "SİL VE YÜKLE" yazın' value={confirmText}
               onChange={(e) => setConfirmText(e.target.value.toLocaleUpperCase("tr-TR"))}
               hint="Bu kurulumdaki mevcut tüm veriler (kullanıcılar hariç) silinip yedekteki veriler yüklenir." />
        <button type="button" className="primary-button tone-danger-btn" data-testid="backup-restore-button"
                disabled={!file || confirmText !== "SİL VE YÜKLE" || busy} onClick={restore}>
          {busy ? "Yükleniyor…" : "Yedeği yükle"}
        </button>
      </div>
      {msg && <div className="sync-msg" data-testid="backup-msg">{msg}</div>}
    </div>
  );
}

export function SettingsModal({ onClose, onSaved, user }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("sp_theme") || "light");
  const [fontSize, setFontSize] = useState(() => localStorage.getItem("sp_font_size") || "normal");
  const [fontWeight, setFontWeight] = useState(() => localStorage.getItem("sp_font_weight") || "normal");
  const [syncMsg, setSyncMsg] = useState("");

  const applyAll = (t, s, w) => {
    setTheme(t); setFontSize(s); setFontWeight(w);
    localStorage.setItem("sp_theme", t);
    localStorage.setItem("sp_font_size", s);
    localStorage.setItem("sp_font_weight", w);
    document.documentElement.setAttribute("data-theme", t);
    document.documentElement.setAttribute("data-font-size", s);
    document.documentElement.setAttribute("data-font-weight", w);
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const p = r.data?.preferences;
      if (p && (p.theme || p.font_size || p.font_weight)) {
        applyAll(p.theme || "light", p.font_size || "normal", p.font_weight || "normal");
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const save = (t, s, w) => {
    applyAll(t, s, w);
    api.put("/users/me/preferences", { theme: t, font_size: s, font_weight: w })
      .then(() => setSyncMsg("Hesabınıza kaydedildi"))
      .catch(() => setSyncMsg("Yalnızca bu cihaza kaydedildi"));
  };



  const submit = (e) => {
    e.preventDefault();
    onSaved?.();
    onClose();
  };

  return (
    <Modal title="Sistem ayarları" onClose={onClose} testid="settings-modal" size="lg">
      <form onSubmit={submit}>
        <div className="setting-section">
          <div className="setting-label">
            <strong>Görünüm</strong>
            <small>Uygulama renk temasını seçin — tercihiniz hesabınıza kaydedilir</small>
          </div>
          <div className="theme-toggle" data-testid="theme-toggle">
            <button type="button" data-testid="theme-light"
                    className={`theme-option ${theme === "light" ? "active" : ""}`}
                    onClick={() => save("light", fontSize, fontWeight)}>
              <span className="theme-swatch theme-swatch-light" />
              <div>
                <strong>Açık Tema</strong>
                <small>Beyaz zemin, parlak renkler</small>
              </div>
            </button>
            <button type="button" data-testid="theme-dark"
                    className={`theme-option ${theme === "dark" ? "active" : ""}`}
                    onClick={() => save("dark", fontSize, fontWeight)}>
              <span className="theme-swatch theme-swatch-dark" />
              <div>
                <strong>Koyu Tema</strong>
                <small>Göz dostu, az ışıklı ortamlar için</small>
              </div>
            </button>
          </div>
        </div>

        <div className="setting-section">
          <div className="setting-label">
            <strong>Yazı boyutu</strong>
            <small>Ekrandaki yazıları büyütüp küçültün</small>
          </div>
          <div className="font-toggle" data-testid="font-size-toggle">
            <button type="button" data-testid="font-size-small"
                    className={`font-option ${fontSize === "small" ? "active" : ""}`}
                    onClick={() => save(theme, "small", fontWeight)}>
              <span className="font-preview" style={{ fontSize: 13 }}>Aa</span>
              <div><strong>Küçük</strong><small>Daha sık</small></div>
            </button>
            <button type="button" data-testid="font-size-normal"
                    className={`font-option ${fontSize === "normal" ? "active" : ""}`}
                    onClick={() => save(theme, "normal", fontWeight)}>
              <span className="font-preview" style={{ fontSize: 16 }}>Aa</span>
              <div><strong>Normal</strong><small>Varsayılan</small></div>
            </button>
            <button type="button" data-testid="font-size-large"
                    className={`font-option ${fontSize === "large" ? "active" : ""}`}
                    onClick={() => save(theme, "large", fontWeight)}>
              <span className="font-preview" style={{ fontSize: 19 }}>Aa</span>
              <div><strong>Büyük</strong><small>Kolay okunur</small></div>
            </button>
          </div>
        </div>

        <div className="setting-section">
          <div className="setting-label">
            <strong>Yazı kalınlığı</strong>
            <small>Yazıların inceliğini/kalınlığını ayarlayın</small>
          </div>
          <div className="font-toggle" data-testid="font-weight-toggle">
            <button type="button" data-testid="font-weight-thin"
                    className={`font-option ${fontWeight === "thin" ? "active" : ""}`}
                    onClick={() => save(theme, fontSize, "thin")}>
              <span className="font-preview" style={{ fontWeight: 400, fontSize: 16 }}>Aa</span>
              <div><strong>İnce</strong><small>Hafif</small></div>
            </button>
            <button type="button" data-testid="font-weight-normal"
                    className={`font-option ${fontWeight === "normal" ? "active" : ""}`}
                    onClick={() => save(theme, fontSize, "normal")}>
              <span className="font-preview" style={{ fontWeight: 500, fontSize: 16 }}>Aa</span>
              <div><strong>Normal</strong><small>Varsayılan</small></div>
            </button>
            <button type="button" data-testid="font-weight-bold"
                    className={`font-option ${fontWeight === "bold" ? "active" : ""}`}
                    onClick={() => save(theme, fontSize, "bold")}>
              <span className="font-preview" style={{ fontWeight: 700, fontSize: 16 }}>Aa</span>
              <div><strong>Kalın</strong><small>Belirgin</small></div>
            </button>
          </div>
        </div>

        {syncMsg && <div className="sync-msg" data-testid="prefs-sync-msg">{syncMsg}</div>}
        {user?.role === "admin" && <BackupSection />}
        <ModalActions onCancel={onClose} submitLabel="Kapat" />
      </form>
    </Modal>
  );
}

// ---------- Transaction / Cheque detail (drill-down) ----------
export function TransactionDetailModal({ transaction, customers = [], accounts = [], onClose, onOpenInvoice }) {
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [linkedPair, setLinkedPair] = useState(null);
  const invId = transaction?.invoice_id || transaction?.related_invoice_id;

  useEffect(() => {
    if (!invId) return;
    api.get(`/invoices?scope=all`).then((r) => {
      const inv = r.data.find((x) => x.id === invId);
      if (inv) setLinkedInvoice(inv);
    }).catch(() => {});
  }, [invId]);

  useEffect(() => {
    if (!transaction?.proxy_link_id) return;
    api.get(`/transactions`).then((r) => {
      const pair = r.data.find((t) => t.proxy_link_id === transaction.proxy_link_id && t.id !== transaction.id);
      if (pair) setLinkedPair(pair);
    }).catch(() => {});
  }, [transaction?.proxy_link_id, transaction?.id]);

  if (!transaction) return null;
  const customer = transaction.customer_id ? customers.find((c) => c.id === transaction.customer_id) : null;
  const account = transaction.account_id ? accounts.find((a) => a.id === transaction.account_id) : null;
  const pairCustomer = linkedPair?.customer_id ? customers.find((c) => c.id === linkedPair.customer_id) : null;

  return (
    <Modal title="Hareket detayı" size="md" onClose={onClose} testid="tx-detail-modal">
      <div className="view-grid">
        <div><span>Tarih</span><strong>{humanDate(transaction.date)}</strong></div>
        <div><span>Yön</span><strong className={transaction.direction === "in" ? "positive" : "negative"}>
          {transaction.direction === "in" ? "Giriş / Tahsilat" : "Çıkış / Ödeme"}
        </strong></div>
        <div><span>Tutar</span><strong className={transaction.direction === "in" ? "positive" : "negative"}>
          {transaction.direction === "in" ? "+" : "-"}{money(transaction.amount)}
        </strong></div>
        <div><span>Kategori</span><strong>{transaction.category || "—"}</strong></div>
        {account && <div><span>Banka / Kasa</span><strong>{account.name}</strong></div>}
        {!account && <div><span>Hesap</span><strong>Cari Hesap üzerinden</strong></div>}
        {customer && <div><span>Cari</span><strong data-testid="tx-detail-customer">{customer.name}</strong></div>}
        {transaction.balance_target && <div><span>Bakiye hedefi</span><strong>{transaction.balance_target}</strong></div>}
      </div>
      {transaction.description && (
        <div className="modal-sub" style={{ marginTop: 12, padding: 12, background: "#f8fafc", borderRadius: 8 }}>
          <strong>Açıklama:</strong> {transaction.description}
        </div>
      )}
      {linkedPair && (
        <div className="modal-sub" style={{ marginTop: 12, padding: 12, background: "#eff6ff", borderRadius: 8, borderLeft: "3px solid #17a9d8" }} data-testid="tx-detail-linked-pair">
          <strong>Cari Hesap bağlantılı hareketi:</strong> {pairCustomer?.name || "—"} ·
          {linkedPair.direction === "in" ? " Giriş" : " Çıkış"} · {money(linkedPair.amount)}
        </div>
      )}
      {linkedInvoice && (
        <div className="modal-actions" style={{ marginTop: 16 }}>
          <button type="button" className="secondary-button" onClick={onClose}>Kapat</button>
          <button data-testid="tx-detail-open-invoice" type="button" className="primary-button"
                  onClick={() => { onClose(); onOpenInvoice?.(linkedInvoice); }}>
            Fatura {linkedInvoice.invoice_no} → aç <ArrowUpRight size={14} />
          </button>
        </div>
      )}
      {!linkedInvoice && (
        <div className="modal-actions" style={{ marginTop: 16 }}>
          <button type="button" className="secondary-button" onClick={onClose}>Kapat</button>
        </div>
      )}
    </Modal>
  );
}

export function ChequeDetailModal({ cheque, customers = [], onClose }) {
  if (!cheque) return null;
  const customer = cheque.customer_id ? customers.find((c) => c.id === cheque.customer_id) : null;
  return (
    <Modal title={`${cheque.direction === "received" ? "Alınan" : "Verilen"} çek · ${cheque.check_no || "-"}`}
           size="md" onClose={onClose} testid="cheque-detail-modal">
      <div className="view-grid">
        <div><span>Çek no</span><strong>{cheque.check_no || "—"}</strong></div>
        <div><span>Yön</span><strong>{cheque.direction === "received" ? "Alınan" : "Verilen"}</strong></div>
        <div><span>Banka / Şube</span><strong>{[cheque.bank_name, cheque.branch].filter(Boolean).join(" / ") || "—"}</strong></div>
        <div><span>Vade</span><strong>{humanDate(cheque.maturity_date)}</strong></div>
        <div><span>Veriliş</span><strong>{humanDate(cheque.issue_date)}</strong></div>
        <div><span>Tutar</span><strong>{money(cheque.amount)}</strong></div>
        {customer && <div><span>Cari</span><strong>{customer.name}</strong></div>}
        <div><span>Durum</span><strong>{cheque.status || "—"}</strong></div>
      </div>
      {cheque.note && (
        <div className="modal-sub" style={{ marginTop: 12, padding: 12, background: "#f8fafc", borderRadius: 8 }}>
          <strong>Not:</strong> {cheque.note}
        </div>
      )}
      <div className="modal-actions" style={{ marginTop: 16 }}>
        <button type="button" className="secondary-button" onClick={onClose}>Kapat</button>
      </div>
    </Modal>
  );
}

